"use client"

import { usePathname } from "next/navigation"
import { useMemo, useState, useEffect, useRef, useCallback, type CSSProperties } from "react"
import type { LucideIcon } from "lucide-react"
import {
  Search,
  Code,
  Building2,
  GraduationCap,
  Plane,
  Play,
  LayoutDashboard,
  GitBranch,
  Wallet,
  HeartPulse,
  Database,
  Brain,
  HardDrive,
  Users,
  Newspaper,
  MessageSquare,
  Calendar,
  Mail,
  User,
  ClipboardCheck,
  ClipboardList,
  Download,
  FileText,
  Globe,
  Camera,
  Compass,
  ShoppingCart,
  BookOpen,
  PenTool,
  BarChart3,
  Boxes,
  Terminal,
  Eye,
  TrendingUp,
  Activity,
  Target,
  PieChart,
  Briefcase,
  Package,
  FolderOpen,
  Trash2,
  CreditCard,
  Home,
  History,
  Cpu,
  Layers,
  Rocket,
  GitCompare,
  ShieldAlert,
  Sparkles,
  Tv,
  FileSearch,
  Map,
  Wrench,
  GitMerge,
  Lock,
  Server,
  Store,
  Share2,
  Star,
  QrCode,
  Ship,
  Satellite,
  CloudRain,
  Zap,
  Mountain,
  Flame,
  Wind,
  Waves,
  Bug,
  Music,
  Radio,
  Upload,
  Trophy,
  Heart,
  AudioLines,
  Phone,
  PanelLeft,
  ChevronDown,
  ChevronRight,
  ChevronsUpDown,
  Pin,
  Plus,
  Check,
  Settings,
  X,
  LocateFixed,
  Bell,
  LogOut,
  BadgeCheck,
  DollarSign,
  MonitorPlay,
  ShoppingBag,
  UserSearch,
  Tag,
  Ticket,
  Shield,
  ShieldCheck,
  Coins,
  Network,
} from "lucide-react"
import type { NavItem } from "../components/app-sidebar"
import { createPortal } from "react-dom"
import { getSidebarUser, getAuthToken as readAuthToken, clearAuthToken, getLoginAppUrl } from "./auth"
import { setSharedCookie, getSharedCookie } from "./utils"
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "../ui/tooltip"
import { SearchDialog, type SearchItem } from "../components/search-dialog"
import { useIsMobile } from "../hooks/use-mobile"

// ── Admin allowlist ──────────────────────────────────────────────────────
// Users whose email appears here can see hidden sidebar sections.
const ADMIN_EMAILS: string[] = ["paul@pvarghese.com"]

function isAdminUser(): boolean {
  if (typeof window === "undefined") return false
  const user = getSidebarUser()
  if (!user || !user.email) return false
  return ADMIN_EMAILS.includes(user.email.toLowerCase())
}

/** Dev ports per app (must match scripts/dev-all.js) */
const DEV_PORTS: Record<string, number> = {
  "login.askfinz.ai": 9500,
  "account.askfinz.ai": 9500,
  "code.askfinz.ai": 9501,
  "dash.askfinz.ai": 9502,
  "data.askfinz.ai": 9503,
  "docs.askfinz.ai": 9504,
  "learn.askfinz.ai": 9505,
  "estate.askfinz.ai": 9506,
  "finance.askfinz.ai": 9507,
  "med.askfinz.ai": 9508,
  "research.askfinz.ai": 9509,
  "search.askfinz.ai": 9510,
  "socials.askfinz.ai": 9511,
  "storage.askfinz.ai": 9512,
  "stream.askfinz.ai": 9513,
  "train.askfinz.ai": 9514,
  "travel.askfinz.ai": 9515,
  "workflow.askfinz.ai": 9516,
  "chat.askfinz.ai": 9521,
  "news.askfinz.ai": 9520,
  "mail.askfinz.ai": 9522,
  "askfinz.ai": 9517,
  "shop.askfinz.ai": 9524,
  "legal.askfinz.ai": 9525,
  "map.askfinz.ai": 9526,
  "create.askfinz.ai": 9527,
  "project.askfinz.ai": 9528,
  "bank.askfinz.ai": 9529,
  "access.askfinz.ai": 9530,
  "feedback.askfinz.ai": 9531,
}

const PROD_BASE: Record<string, string> = {
  "login.askfinz.ai": "https://login.askfinz.ai",
  "askfinz.ai": "https://askfinz.ai",
  "search.askfinz.ai": "https://search.askfinz.ai",
  "code.askfinz.ai": "https://code.askfinz.ai",
  "research.askfinz.ai": "https://research.askfinz.ai",
  "estate.askfinz.ai": "https://estate.askfinz.ai",
  "train.askfinz.ai": "https://train.askfinz.ai",
  "travel.askfinz.ai": "https://travel.askfinz.ai",
  "stream.askfinz.ai": "https://stream.askfinz.ai",
  "docs.askfinz.ai": "https://docs.askfinz.ai",
  "dash.askfinz.ai": "https://dash.askfinz.ai",
  "workflow.askfinz.ai": "https://workflow.askfinz.ai",
  "finance.askfinz.ai": "https://finance.askfinz.ai",
  "learn.askfinz.ai": "https://learn.askfinz.ai",
  "med.askfinz.ai": "https://med.askfinz.ai",
  "data.askfinz.ai": "https://data.askfinz.ai",
  "storage.askfinz.ai": "https://storage.askfinz.ai",
  "socials.askfinz.ai": "https://socials.askfinz.ai",
  "chat.askfinz.ai": "https://chat.askfinz.ai",
  "news.askfinz.ai": "https://news.askfinz.ai",
  "mail.askfinz.ai": "https://mail.askfinz.ai",
  "account.askfinz.ai": "https://account.askfinz.ai",
  "shop.askfinz.ai": "https://shop.askfinz.ai",
  "legal.askfinz.ai": "https://legal.askfinz.ai",
  "map.askfinz.ai": "https://map.askfinz.ai",
  "create.askfinz.ai": "https://create.askfinz.ai",
  "project.askfinz.ai": "https://project.askfinz.ai",
  "bank.askfinz.ai": "https://bank.askfinz.ai",
  "access.askfinz.ai": "https://access.askfinz.ai",
  "feedback.askfinz.ai": "https://feedback.askfinz.ai",
}

function getAppBaseUrl(appKey: string): string {
  if (typeof window !== "undefined") {
    const isLocal = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"
    if (isLocal && DEV_PORTS[appKey] != null) {
      return `${window.location.protocol}//${window.location.hostname}:${DEV_PORTS[appKey]}`
    }
  }
  return PROD_BASE[appKey] ?? ""
}

// ── Sidebar definition ──────────────────────────────────────────────────
// hidden = true  →  only visible to ADMIN_EMAILS users
const GLOBAL_APPS: {
  name: string
  key: string
  icon: LucideIcon
  hidden?: boolean
  pages: { title: string; path: string; icon: LucideIcon; onClick?: () => void; crossAppKey?: string }[]
}[] = [
  // ── Home ────────────────────────────────────────────────────────────
  {
    name: "Home",
    key: "askfinz.ai",
    icon: Home,
    pages: [
      { title: "Home", path: "/", icon: Home },
      { title: "Installers", path: "/installers", icon: Download },
      { title: "Release Notes", path: "/release-notes", icon: FileText },
      { title: "Roadmap", path: "/roadmap", icon: Map },
    ],
  },

  // ── News ────────────────────────────────────────────────────────────
  {
    name: "News",
    key: "news.askfinz.ai",
    icon: Newspaper,
    pages: [
      { title: "Latest News", path: "/", icon: Newspaper },
      { title: "Live News Feed", path: "/live-tv/guide", icon: Tv },
    ],
  },

  // ── Chat ────────────────────────────────────────────────────────────
  {
    name: "Chat",
    key: "chat.askfinz.ai",
    icon: MessageSquare,
    pages: [
      { title: "Home",         path: "/",        icon: Home },
      { title: "New Chat",     path: "/new",      icon: Sparkles },
      { title: "Card Library", path: "/library",  icon: Layers },
      { title: "All History",  path: "/recent",   icon: History },
    ],
  },

  // ── Create ──────────────────────────────────────────────────────────
  {
    name: "Create",
    key: "create.askfinz.ai",
    icon: PenTool,
    pages: [
      { title: "Studio", path: "/", icon: PenTool },
      { title: "Image", path: "/image", icon: Sparkles },
      { title: "Video", path: "/video", icon: Play },
      { title: "Audio", path: "/audio", icon: AudioLines },
      { title: "UI/UX", path: "/ui", icon: Layers },
      { title: "Avatar", path: "/avatar", icon: Boxes },
      { title: "Library", path: "/library", icon: FolderOpen },
      { title: "Models", path: "/models", icon: Brain },
    ],
  },

  // ── Search ──────────────────────────────────────────────────────────
  {
    name: "Search",
    key: "search.askfinz.ai",
    icon: Search,
    pages: [
      { title: "Web Search", path: "/browser", icon: Globe },
      { title: "Knowledge Search", path: "/", icon: Search },
      { title: "Index Review", path: "/index-reviewer", icon: ClipboardCheck },
      { title: "Indexing Progress", path: "/indexing_in_progress", icon: Activity, crossAppKey: "askfinz.ai" },
    ],
  },

  // ── Map ─────────────────────────────────────────────────────────────
  // The map app is a single-page SPA — `?view=` switches the right-rail
  // panel without remounting. This entry surfaces the genuinely
  // distinct workflows; the niche rails (Power's spatial-ops, Trips
  // placeholder, You) stay reachable from inside the app via the rail
  // switcher but are dropped from the sidebar to reduce clutter.
  {
    name: "Map",
    key: "map.askfinz.ai",
    icon: Map,
    pages: [
      { title: "Home",         path: "/",              icon: Home },
      { title: "Overlays",     path: "/?view=ops",     icon: Target },
      { title: "Routing",      path: "/?view=route",   icon: GitMerge },
      { title: "AI Tools",     path: "/?view=ai",      icon: Sparkles },
      { title: "Saved Places", path: "/?view=saved",   icon: Star },
    ],
  },

  // ── Map Views ───────────────────────────────────────────────────────
  // Thematic preset bundles — each ?preset= deep-link enables a curated
  // set of overlays + Hybrid basemap in one click. Preset definitions
  // live in `map.askfinz.ai/lib/map-presets.ts` and are consumed by
  // `map-shell.tsx`'s preset-hydration effect.
  {
    name: "Map Views",
    key: "map.askfinz.ai",
    icon: Layers,
    pages: [
      { title: "Aviation",       path: "/?preset=aviation",   icon: Plane },
      { title: "Maritime",       path: "/?preset=maritime",   icon: Ship },
      { title: "Space",          path: "/?preset=space",      icon: Satellite },
      { title: "Weather",        path: "/?preset=weather",    icon: CloudRain },
      { title: "Energy",         path: "/?preset=energy",     icon: Zap },
      { title: "Earth",          path: "/?preset=earth",      icon: Mountain },
      { title: "Events",         path: "/?preset=events",     icon: Flame },
      { title: "Pollution",      path: "/?preset=pollution",  icon: Wind },
      { title: "Oceans",         path: "/?preset=oceans",     icon: Waves },
      { title: "Atmosphere",     path: "/?preset=atmosphere", icon: Sparkles },
      { title: "News",           path: "/?preset=news",       icon: Newspaper },
    ],
  },

  // ── LLM & ML Training (hidden) ─────────────────────────────────────
  {
    name: "LLM & ML Training",
    key: "train.askfinz.ai",
    icon: Brain,
    hidden: true,
    pages: [
      { title: "LLM Model",   path: "/",            icon: Brain },
      { title: "ML Training", path: "/ml-training", icon: Cpu },
      { title: "Datasets",    path: "/datasets",    icon: Database },
      { title: "Fine-Tune",   path: "/fine-tune",   icon: Sparkles },
    ],
  },

  // ── Storage ─────────────────────────────────────────────────────────
  // Order matches the askFinz design's File Storage sidebar: Overview is the
  // landing page (KPIs + storage-by-category + recent activity), then the
  // file/share/leads/trash surfaces, then the quick-access filters (Starred /
  // Recents) which are filtered views of the same My Files list.
  {
    name: "Storage",
    key: "storage.askfinz.ai",
    icon: HardDrive,
    pages: [
      { title: "Overview",    path: "/",            icon: LayoutDashboard },
      { title: "My Files",    path: "/files",       icon: FolderOpen },
      { title: "Shared",      path: "/shared",      icon: Share2 },
      { title: "File Leads",  path: "/leads",       icon: FileSearch },
      { title: "Recycle Bin", path: "/recycle-bin", icon: Trash2 },
      { title: "Starred",     path: "/starred",     icon: Star },
      { title: "Recents",     path: "/recents",     icon: History },
      // Per-device file browser — reachable from /devices/[id]/files. The
      // top-level entry below is a stub deep-link; the real navigation
      // lives in access.askfinz.ai (Fleet page) where users pick a device.
      { title: "Device Files", path: "/devices",     icon: Server, crossAppKey: "access.askfinz.ai" },
    ],
  },

  // ── Mail & Calendar ─────────────────────────────────────────────────
  // Unified inbox + calendar across linked Google / Microsoft / IMAP accounts.
  {
    name: "Mail & Calendar",
    key: "mail.askfinz.ai",
    icon: Mail,
    pages: [
      { title: "Inbox",    path: "/",           icon: Mail },
      { title: "Starred",  path: "/starred",    icon: Sparkles },
      { title: "Sent",     path: "/sent",       icon: Sparkles },
      { title: "Drafts",   path: "/drafts",     icon: FileText },
      { title: "Calendar", path: "/calendar",   icon: Calendar },
      { title: "Accounts", path: "/accounts",   icon: User },
    ],
  },

  // ── Project ─────────────────────────────────────────────────────────
  // Sub-pages mirror the in-app tab bar in components/project-shell.tsx —
  // WORK / PLAN / ITEMS / PEOPLE / INSIGHT groups, flattened.
  {
    name: "Project",
    key: "project.askfinz.ai",
    icon: Briefcase,
    pages: [
      { title: "Overview",     path: "/",              icon: LayoutDashboard },
      { title: "Tasks",        path: "/tasks",         icon: ClipboardCheck },
      { title: "Kanban",       path: "/kanban",        icon: Layers },
      { title: "Timeline",     path: "/timeline",      icon: GitBranch },
      { title: "Networks",     path: "/networks",      icon: GitMerge },
      { title: "Budget",       path: "/budget",        icon: Wallet },
      { title: "Capacity",     path: "/capacity",      icon: Activity },
      { title: "Automations",  path: "/automations",   icon: Sparkles },
      { title: "Incidents",    path: "/incidents",     icon: ShieldAlert },
      { title: "Team",         path: "/team",          icon: Users },
      { title: "Stakeholders", path: "/stakeholders",  icon: Briefcase },
      { title: "Reports",      path: "/reports",       icon: BarChart3 },
    ],
  },

  // ── Shop ────────────────────────────────────────────────────────────
  {
    name: "Shop",
    key: "shop.askfinz.ai",
    icon: ShoppingCart,
    pages: [
      { title: "Discover", path: "/", icon: Compass },
      { title: "Storefronts", path: "/vendors", icon: Store },
      { title: "Cart", path: "/cart", icon: ShoppingCart },
      { title: "Orders", path: "/orders", icon: Package },
      { title: "Saved", path: "/saved", icon: Heart },
      { title: "Seller console", path: "/seller", icon: BarChart3 },
    ],
  },

  // ── Social Media (hidden) ───────────────────────────────────────────
  {
    name: "Social Media",
    key: "socials.askfinz.ai",
    icon: Users,
    hidden: true,
    pages: [
      { title: "Feed", path: "/", icon: Newspaper },
      { title: "Posts", path: "/posts", icon: MessageSquare },
      { title: "Events", path: "/events", icon: Calendar },
      { title: "Inbox", path: "/inbox", icon: Mail },
      { title: "Search", path: "/search", icon: Search },
      { title: "Profile", path: "/profile", icon: User },
    ],
  },

  // ── Research ────────────────────────────────────────────────────────
  // See docs/22-Research-Platform/MASTER_BUILD_PLAN.md §4.3 for the route map.
  {
    name: "Research",
    key: "research.askfinz.ai",
    icon: BookOpen,
    pages: [
      { title: "Projects", path: "/projects", icon: FolderOpen },
      { title: "Library", path: "/library", icon: BookOpen },
      { title: "Templates", path: "/templates", icon: Layers },
    ],
  },

  // ── Coder ──────────────────────────────────────────────────────────
  {
    name: "Coder",
    key: "code.askfinz.ai",
    icon: Code,
    pages: [
      { title: "webIDE", path: "/", icon: Terminal },
      { title: "Skills", path: "/skills", icon: Sparkles },
      { title: "Tools", path: "/tools", icon: Wrench },
      { title: "Git Manager", path: "/reo", icon: GitMerge },
      { title: "Repositories", path: "/repositories", icon: GitBranch },
      { title: "Knowledge Base", path: "/knowledge", icon: BookOpen },
      { title: "Publisher", path: "/publisher", icon: Rocket },
      { title: "Deployments", path: "/deployments", icon: Server },
      { title: "Secrets", path: "/secrets", icon: Lock },
      { title: "Monitor", path: "/monitor", icon: Eye },
      { title: "Analytics", path: "/analytics", icon: BarChart3 },
      { title: "Templates", path: "/templates", icon: FolderOpen },
      { title: "Marketplace", path: "/marketplace", icon: Store },
      { title: "Cron Agents", path: "/cron", icon: Calendar },
    ],
  },

  // ── Access (askFinz OS operator console) ───────────────────────────
  // Operator surface for paired askFinz OS devices: fleet, clustering,
  // build/deploy, SSH cert minting, audit. Per-device pages (overview,
  // workloads, vms, vms/new wizard, terminal, screen, packages, users,
  // audit, ssh-cert, hardware, storage, network, services, logs,
  // local-llm, settings) are reached by clicking a device card on the
  // Fleet page. Cluster join wizard at /cluster/join.
  {
    name: "Access",
    key: "access.askfinz.ai",
    icon: Server,
    pages: [
      { title: "Fleet",       path: "/",            icon: LayoutDashboard },
      { title: "Cluster",     path: "/cluster",     icon: Layers },
      { title: "Marketplace", path: "/marketplace", icon: Store },
      { title: "Deploy",      path: "/deploy",      icon: Rocket },
      { title: "Events",      path: "/events",      icon: Activity },
      { title: "Pools",       path: "/pools",       icon: Briefcase },
      { title: "API Keys",    path: "/api-keys",    icon: Lock },
      { title: "SSH Certs",   path: "/ssh",         icon: Lock },
      { title: "Audit Log",   path: "/audit",       icon: FileSearch },
      { title: "Pair Device", path: "/link",        icon: QrCode },
    ],
  },

  // ── Dash (internal admin panel) ────────────────────────────────────────
  // MOVED: the Dash group now renders as the admin-only "askFinz Portal"
  // collapsible in the sidebar footer (see components/app-sidebar.tsx,
  // PORTAL_ITEMS). It is intentionally NOT a GLOBAL_APPS entry anymore so it
  // doesn't appear in the main app list.

  // ── Data Analytics (hidden) ─────────────────────────────────────────
  {
    name: "Data Analytics",
    key: "data.askfinz.ai",
    icon: Database,
    hidden: true,
    pages: [
      { title: "Data Analytics", path: "/", icon: Database },
    ],
  },

  // ── Charts (self-serve dashboards / reporting) ──────────────────────
  {
    name: "Charts",
    key: "charts.askfinz.ai",
    icon: BarChart3,
    pages: [
      { title: "Dashboards", path: "/", icon: BarChart3 },
    ],
  },

  // ── Knowledge (unified integrations / knowledge layer) ──────────────
  {
    name: "Knowledge",
    key: "knowledge.askfinz.ai",
    icon: Boxes,
    pages: [
      { title: "Knowledge", path: "/", icon: Boxes },
    ],
  },

  // ── Comms (admin-only: Telnyx calls & SMS audit on dash) ────────────
  {
    name: "Comms",
    key: "dash.askfinz.ai",
    icon: Phone,
    hidden: true,
    pages: [
      { title: "Calls & SMS", path: "/comms", icon: Phone },
    ],
  },

  // ── Legal ───────────────────────────────────────────────────────────
  {
    name: "Legal",
    key: "legal.askfinz.ai",
    icon: FileText,
    pages: [
      { title: "Case Law", path: "/", icon: FileSearch },
      { title: "Contracts", path: "/contracts", icon: FileText },
      { title: "Matters", path: "/matters", icon: FolderOpen },
      { title: "Templates", path: "/templates", icon: Layers },
    ],
  },

  // ── Medical (hidden) ────────────────────────────────────────────────
  {
    name: "Medical",
    key: "med.askfinz.ai",
    icon: HeartPulse,
    hidden: true,
    pages: [
      { title: "Medical", path: "/", icon: HeartPulse },
    ],
  },

  // ── Learn ───────────────────────────────────────────────────────────
  {
    name: "Learn",
    key: "learn.askfinz.ai",
    icon: GraduationCap,
    pages: [
      // "Overview" = all-courses home (was "Dashboard"). "Current course" jumps
      // into the course you last opened (was "Overview"; /overview redirects to
      // the last-viewed course). The rest are cross-course analytics pages.
      { title: "Overview",       path: "/",          icon: LayoutDashboard },
      { title: "Current course", path: "/overview",  icon: GraduationCap },
      { title: "Resources",      path: "/resources", icon: FileText },
      { title: "Study",          path: "/study",     icon: MessageSquare },
      { title: "Exam",           path: "/exam",      icon: ClipboardList },
      { title: "Knowledge Bank",  path: "/knowledge-bank", icon: Database },
      { title: "Progress",       path: "/progress",  icon: BarChart3 },
    ],
  },

  // ── Real Estate (hidden) ────────────────────────────────────────────
  {
    name: "Real Estate",
    key: "estate.askfinz.ai",
    icon: Building2,
    hidden: true,
    pages: [
      { title: "Real Estate", path: "/", icon: Building2 },
    ],
  },

  // ── Bank ────────────────────────────────────────────────────────────
  {
    name: "Bank",
    key: "bank.askfinz.ai",
    icon: CreditCard,
    pages: [
      { title: "Overview", path: "/", icon: Wallet },
      { title: "Accounts", path: "/accounts", icon: CreditCard },
      { title: "Transactions", path: "/transactions", icon: Activity },
      { title: "Transfers", path: "/transfers", icon: GitMerge },
      { title: "Bills", path: "/bills", icon: Calendar },
      { title: "Budgets", path: "/budgets", icon: BarChart3 },
    ],
  },

  // ── Finance (askFinz market terminal) ───────────────────────────────
  {
    name: "Finance",
    key: "finance.askfinz.ai",
    icon: Wallet,
    // Ordered by IA family (see docs/.../INFORMATION-ARCHITECTURE.md):
    // Markets · Economy · News · Security (/stock) · Discover · Crypto · Tools · You
    pages: [
      // Markets
      { title: "Today's Market", path: "/", icon: TrendingUp },
      { title: "Market Movers", path: "/markets/movers", icon: Activity },
      { title: "Heatmap", path: "/markets/heatmap", icon: Boxes },
      { title: "Market Breadth", path: "/markets/breadth", icon: BarChart3 },
      { title: "World Markets", path: "/markets/world", icon: Globe },
      { title: "Charts Wall", path: "/markets/wall", icon: Boxes },
      // Economy
      { title: "Macro", path: "/economy", icon: Globe },
      { title: "Explorer", path: "/economy/explorer", icon: Compass },
      { title: "Economic Calendar", path: "/economy/calendar", icon: Calendar },
      // News (full reader lives on the news.askfinz.ai app; per-ticker news is in Stock Detail)
      { title: "Money Flows", path: "/news/flows", icon: GitMerge },
      // Security (/stock/[symbol]/…)
      { title: "Stock Detail", path: "/stock", icon: Briefcase },
      { title: "Advanced Chart", path: "/stock/chart", icon: TrendingUp },
      { title: "Options", path: "/stock/options", icon: PieChart },
      { title: "Level 2 · Tape", path: "/stock/depth", icon: Activity },
      { title: "Relationship Map", path: "/stock/relations", icon: Share2 },
      { title: "Comparison", path: "/stock/compare", icon: GitCompare },
      // Discover
      { title: "Screener", path: "/screener", icon: FileSearch },
      { title: "ETF Analytics", path: "/screener/etf", icon: Package },
      { title: "IPO Center", path: "/screener/ipo", icon: Rocket },
      // Crypto
      { title: "Crypto · FX Desk", path: "/crypto", icon: Coins },
      // Tools
      { title: "Backtester", path: "/backtest", icon: Cpu },
      // You
      { title: "My Dashboards", path: "/dashboards", icon: Target },
      { title: "Workspace", path: "/dashboards/workspace", icon: FolderOpen },
      { title: "Portfolio", path: "/portfolio", icon: Wallet },
      { title: "Alerts", path: "/alerts", icon: Bell },
    ],
  },

  // ── Streaming & Entertainment ───────────────────────────────────────
  // askFinz live + sound media platform (UGC video VOD + music + OBS live).
  // Mirrors the in-app surfaces grafted into AppShell (the design Shell
  // sidebar is dropped, so these live here). See docs/30-Stream-Media-Platform.
  {
    name: "Streaming",
    key: "stream.askfinz.ai",
    icon: Play,
    pages: [
      { title: "Overview",     path: "/",             icon: Home },
      { title: "Browse",       path: "/browse",       icon: Compass },
      { title: "Following",    path: "/following",    icon: Heart },
      { title: "Music",        path: "/music",        icon: Music },
      { title: "Studio",       path: "/studio",       icon: Radio },
      { title: "Upload video", path: "/upload",       icon: Upload },
      { title: "Upload music", path: "/music/upload", icon: AudioLines },
      { title: "Your channel", path: "/channel",      icon: User },
    ],
  },

  // ── Travel ──────────────────────────────────────────────────────────
  // Order mirrors the Claude travel design (tv-app.jsx NAV_GROUPS):
  //   Plan   — My trips · Trip overview · Itinerary · Map · Suggestions
  //   Book   — Flights · Stays · Vehicles · Activities
  //   Manage — Budget · Packing · Travelers
  //   AI     — AI Trip Builder
  // Per-trip routes resolve via app/(app)/{overview,itinerary,map,budget,
  // packing,travelers}/page.tsx which read activeTripId from the travel
  // store and replace() into /trips/<id>/<section>.
  {
    name: "Travel",
    key: "travel.askfinz.ai",
    icon: Plane,
    pages: [
      { title: "My trips",         path: "/",            icon: Compass },
      { title: "Trip overview",    path: "/overview",    icon: LayoutDashboard },
      { title: "Itinerary",        path: "/itinerary",   icon: Calendar },
      { title: "Map",              path: "/map",         icon: Map },
      { title: "Trip suggestions", path: "/templates",   icon: Layers },
      { title: "Flights",          path: "/flights",     icon: Plane },
      { title: "Stays",            path: "/stays",       icon: Building2 },
      { title: "Vehicles",         path: "/vehicles",    icon: Wrench },
      { title: "Activities",       path: "/activities",  icon: Camera },
      { title: "Budget",           path: "/budget",      icon: Wallet },
      { title: "Packing",          path: "/packing",     icon: Briefcase },
      { title: "Travelers",        path: "/travelers",   icon: Users },
      { title: "AI Trip Builder",  path: "/build",       icon: Sparkles },
    ],
  },

  // ── Workflow ────────────────────────────────────────────────────────
  {
    name: "Workflow",
    key: "workflow.askfinz.ai",
    icon: GitBranch,
    pages: [
      { title: "Overview", path: "/", icon: LayoutDashboard },
      { title: "Flow Builder", path: "/builder/", icon: GitBranch },
    ],
  },
]

// ── Category taxonomy ───────────────────────────────────────────────────
// Mirrors askfinz.com `lib/apps-registry.ts` APP_GROUPS (Core / Tools /
// Verticals). Apps are grouped under these labels in the sidebar and sorted
// by projected popularity within each group. Keep in sync with the marketing
// registry: Create=core, Train+Workflow=vertical (re-categorised 2026-05).
type SidebarCategory = "home" | "core" | "tools" | "vertical"

const CATEGORY_LABEL: Record<SidebarCategory, string> = {
  home: "",
  core: "Core",
  tools: "Tools",
  vertical: "Verticals",
}

const CATEGORY_RANK: Record<SidebarCategory, number> = {
  home: 0, core: 1, tools: 2, vertical: 3,
}

// Group membership, keyed by app `name` (unique — distinguishes "Map" vs
// "Map Views"). Anything unlisted falls back to "vertical".
const APP_CATEGORY: Record<string, SidebarCategory> = {
  "Home": "home",
  // Core
  "Chat": "core", "Create": "core", "Search": "core", "News": "core", "Research": "core", "Coder": "core",
  // Tools
  "Mail & Calendar": "tools", "Storage": "tools", "Map": "tools", "Map Views": "tools", "Project": "tools", "Access": "tools",
  "Charts": "tools", "Knowledge": "tools", "Comms": "tools",
  // Verticals
  "Travel": "vertical", "Bank": "vertical", "Finance": "vertical", "Social Media": "vertical", "Shop": "vertical",
  "Streaming": "vertical", "Education & Learning": "vertical", "Medical": "vertical",
  "Real Estate": "vertical", "Data Analytics": "vertical", "Workflow": "vertical", "Legal": "vertical",
  "LLM & ML Training": "vertical",
}

// Within-category order — lower renders higher. Sorted by projected
// popularity / breadth of demand. Unlisted → 999 (sinks to the bottom).
const APP_ORDER: Record<string, number> = {
  "Home": 0,
  // Core
  "Chat": 0, "Create": 1, "Search": 2, "News": 3, "Research": 4, "Coder": 5,
  // Tools
  "Mail & Calendar": 0, "Storage": 1, "Map": 2, "Map Views": 3, "Project": 4, "Access": 5, "Charts": 6, "Knowledge": 7, "Comms": 8,
  // Verticals
  "Travel": 0, "Bank": 1, "Finance": 2, "Social Media": 3, "Shop": 4, "Streaming": 5,
  "Education & Learning": 6, "Medical": 7, "Real Estate": 8, "Data Analytics": 9, "Workflow": 10,
  "Legal": 11, "LLM & ML Training": 12,
}

function appCategory(name: string): SidebarCategory {
  return APP_CATEGORY[name] ?? "vertical"
}

/** Stable sort key: category rank first, then popularity order within it. */
function appSortKey(name: string): number {
  return CATEGORY_RANK[appCategory(name)] * 1000 + (APP_ORDER[name] ?? 999)
}

export function getGlobalSidebarNav(currentOrigin: string, pathname: string): NavItem[] {
  // Do NOT fall back to window.location.origin — callers pass "" during SSR/hydration.
  // Accessing window here causes server/client mismatch → React #418.
  const origin = currentOrigin.replace(/\/$/, "")
  // Only read client-only APIs (localStorage, window) after origin is resolved (post-mount).
  // During SSR and the initial hydration render, origin="" and these must return safe defaults.
  // Only use the token for cross-app URL handoff if it is a real JWT.
  // A non-JWT value (the string "null", a "handoff:…" placeholder, an opaque
  // session id) would produce ?token=<garbage>, which the backend rejects as a
  // 401 Bearer token even though the HttpOnly cookie is valid — triggering an
  // unwanted logout. We validate here so nav links never embed a bad token.
  const rawToken = origin && typeof window !== "undefined" ? localStorage.getItem("AskFinz_auth_token") : null
  const token = rawToken && rawToken.split(".").length === 3 && rawToken.startsWith("eyJ") ? rawToken : null
  const admin = origin ? isAdminUser() : false

  return GLOBAL_APPS
    .filter((app) => !app.hidden || admin)
    // Order by Core → Tools → Verticals, then by popularity within each group.
    .slice()
    .sort((a, b) => appSortKey(a.name) - appSortKey(b.name))
    .map((app) => {
      const baseUrl = getAppBaseUrl(app.key)
      const currentApp = origin === baseUrl
      const isActive =
        currentApp &&
        app.pages.some((p) =>
          p.path === "/" ? pathname === "/" : pathname === p.path || (p.path !== "/" && pathname.startsWith(p.path))
        )

      return {
        title: app.name,
        icon: app.icon,
        isActive: !!isActive,
        category: CATEGORY_LABEL[appCategory(app.name)],
        items: app.pages.map((p) => {
          const itemBase = p.crossAppKey ? getAppBaseUrl(p.crossAppKey) : baseUrl
          // Use relative paths when on the same app — absolute URLs prevent Next.js client-side routing
          const sameDomain = origin !== "" && origin === itemBase
          let url: string
          if (sameDomain) {
            url = p.path.startsWith("/") ? p.path : `/${p.path}`
          } else {
            const fullPath = `${itemBase}${p.path.startsWith("/") ? p.path : `/${p.path}`}`
            url = (origin && origin !== itemBase && token) ? `${fullPath}${fullPath.includes("?") ? "&" : "?"}token=${encodeURIComponent(token)}` : fullPath
          }
          return {
            title: p.title,
            url,
            icon: p.icon,
            onClick: p.onClick,
          }
        }),
      }
    })
}

/**
 * Hook: returns global sidebar nav (all web apps A–Z with pages and icons).
 * Use in any app to show the unified sidebar. Active state is based on current origin and pathname.
 */
export function useGlobalSidebarNav(): NavItem[] {
  const pathname = usePathname()
  const [origin, setOrigin] = useState("")
  const [admin, setAdmin] = useState(false)

  useEffect(() => {
    setOrigin(typeof window !== "undefined" ? window.location.origin : "")

    // Check admin status reactively — user data may load after first render
    const checkAdmin = () => setAdmin(isAdminUser())
    checkAdmin()

    // Re-check when auth state changes (login, cross-subdomain sync, etc.)
    window.addEventListener("authUserUpdated", checkAdmin)
    window.addEventListener("storage", checkAdmin)
    window.addEventListener("focus", checkAdmin)

    // Poll briefly for localStorage propagation from token query params
    const timer = setTimeout(checkAdmin, 1000)

    return () => {
      window.removeEventListener("authUserUpdated", checkAdmin)
      window.removeEventListener("storage", checkAdmin)
      window.removeEventListener("focus", checkAdmin)
      clearTimeout(timer)
    }
  }, [])

  return useMemo(() => getGlobalSidebarNav(origin, pathname ?? ""), [origin, pathname, admin])
}

export interface NavSearchEntry {
  id: string
  title: string
  description?: string
  url: string
  category: string
}

/**
 * Build global search entries from GLOBAL_APPS — every app and every page —
 * so the CMD+K palette automatically covers all web apps and auto-adapts the
 * moment a new app or page is added to the single source of truth above. No
 * separate hand-maintained list to keep in sync.
 *
 * Hidden (admin-only) apps are included only for admin users, matching the
 * sidebar's visibility rules. URLs use each app's base (dev ports locally,
 * prod domains otherwise); cross-app pages resolve via their crossAppKey.
 */
export function getGlobalNavSearchEntries(): NavSearchEntry[] {
  const admin = typeof window !== "undefined" ? isAdminUser() : false
  const entries: NavSearchEntry[] = []
  for (const app of GLOBAL_APPS) {
    if (app.hidden && !admin) continue
    const category = CATEGORY_LABEL[appCategory(app.name)] || "Apps"
    const appBase = getAppBaseUrl(app.key)
    for (const p of app.pages) {
      const base = p.crossAppKey ? getAppBaseUrl(p.crossAppKey) : appBase
      const path = p.path.startsWith("/") ? p.path : `/${p.path}`
      entries.push({
        id: `nav-${app.key}-${p.path}`.replace(/[^a-z0-9]+/gi, "-").toLowerCase(),
        title: p.title === app.name ? app.name : `${app.name} › ${p.title}`,
        description: `${app.name} · ${p.title}`,
        url: `${base}${path}`,
        category,
      })
    }
  }
  return entries
}

// ════════════════════════════════════════════════════════════════════════════
// Sidebar & Nav Wheel redesign (2026-06) — ADDITIVE, self-contained.
//
// Everything ABOVE (GLOBAL_APPS, getGlobalSidebarNav, useGlobalSidebarNav,
// getGlobalNavSearchEntries) is unchanged so the 48 apps + AppShell keep
// working. The exports below render the new design (sidebar + collapsed rail +
// radial navigation wheel + command palette) driven by the SAME GLOBAL_APPS
// data, using lucide icons + shadcn primitives. Currently mounted only by
// template.askfinz.ai.
//
// Design tokens: the design uses bare --accent / --fg / --font-serif color
// vars, but the codebase ships HSL-triplet tokens (--accent: "0 0% 15%"). We
// bridge them into scoped --afn-* custom properties (hsl()-wrapped) so the
// exact design colors render correctly in light + dark without touching any
// other file.
// ════════════════════════════════════════════════════════════════════════════

const AFN_CSS = `
.afn-shell{
  --afn-bg:hsl(var(--background));
  --afn-fg:hsl(var(--foreground));
  --afn-card:hsl(var(--card));
  --afn-border:hsl(var(--border));
  --afn-muted:hsl(var(--muted));
  --afn-muted-fg:hsl(var(--muted-foreground));
  --afn-accent:hsl(var(--accent));
  --afn-accent-fg:hsl(var(--accent-foreground));
  --afn-header-bg:hsl(var(--header-footer-background));
  --afn-good:#16a34a;
  --afn-bad:hsl(var(--destructive));
  --afn-viz-1:#5b6470;
  --afn-font-sans:var(--font-sans, ui-sans-serif, system-ui, -apple-system, sans-serif);
  --afn-font-serif:var(--font-source-serif, ui-serif, Georgia, "Times New Roman", serif);
  --afn-font-mono:var(--font-mono, ui-monospace, "SF Mono", Menlo, monospace);
}
.afn-hover-muted:hover{background:var(--afn-muted) !important}
.afn-hover-border:hover{border-color:var(--afn-border) !important}
.afn-hover-fg:hover{color:var(--afn-fg) !important}
.afn-pin{opacity:.4}
.afn-pin:hover{opacity:1}
.afn-unpin:hover{opacity:.55}
.afn-sb-scroll::-webkit-scrollbar{width:8px}
.afn-sb-scroll::-webkit-scrollbar-thumb{background:var(--afn-border);border-radius:8px}
@keyframes afnWheelIn{from{opacity:0;transform:scale(.95)}to{opacity:1;transform:none}}
@keyframes afnPop{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:none}}
`

type SbSub = { title: string; url: string; icon: LucideIcon }
type SbPage = { title: string; url: string; icon: LucideIcon; active: boolean; path: string; subs?: SbSub[] }
type SbApp = { key: string; name: string; icon: LucideIcon; active: boolean; pages: SbPage[]; badge?: LucideIcon }

// Level-3 sub-pages, keyed "host|pagePath". Drives the outer ring of the nav
// wheel. Only the pages listed here have sub-pages; others show two rings.
const SUBS: Record<string, { title: string; path: string; icon: LucideIcon }[]> = {
  "project.askfinz.ai|/tasks": [["All tasks", "/tasks?all", Layers], ["Assigned to me", "/tasks?mine", User], ["By status", "/tasks?status", ClipboardCheck], ["Archived", "/tasks?archived", Trash2]].map(([title, path, icon]) => ({ title: title as string, path: path as string, icon: icon as LucideIcon })),
  "project.askfinz.ai|/kanban": [["My board", "/kanban?mine", User], ["Sprint", "/kanban?sprint", Target], ["Backlog", "/kanban?backlog", Layers]].map(([title, path, icon]) => ({ title: title as string, path: path as string, icon: icon as LucideIcon })),
  "project.askfinz.ai|/reports": [["Velocity", "/reports/velocity", TrendingUp], ["Burndown", "/reports/burndown", Activity], ["Workload", "/reports/workload", Users]].map(([title, path, icon]) => ({ title: title as string, path: path as string, icon: icon as LucideIcon })),
  "chat.askfinz.ai|/library": [["Prompts", "/library/prompts", Sparkles], ["Snippets", "/library/snippets", Code], ["Personas", "/library/personas", User]].map(([title, path, icon]) => ({ title: title as string, path: path as string, icon: icon as LucideIcon })),
  "code.askfinz.ai|/repositories": [["Active", "/repositories?active", GitBranch], ["Archived", "/repositories?archived", Trash2], ["Forks", "/repositories?forks", GitMerge]].map(([title, path, icon]) => ({ title: title as string, path: path as string, icon: icon as LucideIcon })),
  "code.askfinz.ai|/deployments": [["Production", "/deployments/prod", Rocket], ["Preview", "/deployments/preview", Eye], ["History", "/deployments/history", History]].map(([title, path, icon]) => ({ title: title as string, path: path as string, icon: icon as LucideIcon })),
  "mail.askfinz.ai|/": [["Focused", "/?focused", Target], ["Other", "/?other", Mail], ["Unread", "/?unread", Activity]].map(([title, path, icon]) => ({ title: title as string, path: path as string, icon: icon as LucideIcon })),
  "storage.askfinz.ai|/files": [["Documents", "/files/docs", FileText], ["Images", "/files/images", Camera], ["Code", "/files/code", Code], ["Archives", "/files/archives", Package]].map(([title, path, icon]) => ({ title: title as string, path: path as string, icon: icon as LucideIcon })),
  "travel.askfinz.ai|/stays": [["Hotels", "/stays/hotels", Building2], ["Apartments", "/stays/apartments", Home], ["Saved", "/stays/saved", Heart]].map(([title, path, icon]) => ({ title: title as string, path: path as string, icon: icon as LucideIcon })),
  "travel.askfinz.ai|/flights": [["Outbound", "/flights/out", Plane], ["Return", "/flights/return", Plane], ["Alternatives", "/flights/alt", GitMerge]].map(([title, path, icon]) => ({ title: title as string, path: path as string, icon: icon as LucideIcon })),
  "finance.askfinz.ai|/portfolio": [["Holdings", "/portfolio/holdings", Layers], ["Performance", "/portfolio/perf", TrendingUp], ["Allocation", "/portfolio/alloc", PieChart]].map(([title, path, icon]) => ({ title: title as string, path: path as string, icon: icon as LucideIcon })),
  "bank.askfinz.ai|/transactions": [["Recent", "/transactions?recent", History], ["Pending", "/transactions?pending", Activity], ["Disputed", "/transactions?disputed", ShieldAlert]].map(([title, path, icon]) => ({ title: title as string, path: path as string, icon: icon as LucideIcon })),
}

function buildSubs(host: string, path: string, origin: string, token: string | null): SbSub[] | undefined {
  const defs = SUBS[host + "|" + path]
  if (!defs) return undefined
  return defs.map((s) => ({ title: s.title, icon: s.icon, url: pageHref(host, s.path, undefined, origin, token) }))
}

// Secondary apps that render under the divider at the bottom of the sidebar
// (mirrors the design's "MORE" group + app-sidebar.tsx footer collapsibles).
// askFinz Portal carries a lock badge (admin-secured).
const MORE_DEFS: { key: string; name: string; icon: LucideIcon; badge?: LucideIcon; adminOnly?: boolean; pages: { title: string; path: string; icon: LucideIcon }[] }[] = [
  { key: "docs.askfinz.ai", name: "Dev Docs", icon: BookOpen, pages: [
    { title: "Documentation", path: "/", icon: BookOpen },
  ] },
  // Mirrors FEEDBACK_ITEMS in components/app-sidebar.tsx.
  { key: "feedback.askfinz.ai", name: "Feedback", icon: MessageSquare, pages: [
    { title: "Issues & Bugs", path: "/issues", icon: Bug },
    { title: "Awaiting Verify", path: "/verify", icon: ClipboardCheck },
    { title: "Feature Requests", path: "/requests", icon: MessageSquare },
    { title: "Shipped & Resolved", path: "/shipped", icon: Sparkles },
    { title: "Report Issue", path: "/issues/new", icon: Bug },
    { title: "New Request", path: "/requests/new", icon: MessageSquare },
  ] },
  // Mirrors PORTAL_ITEMS in components/app-sidebar.tsx (admin-only, lock badge).
  { key: "dash.askfinz.ai", name: "askFinz Portal", icon: Shield, badge: Lock, adminOnly: true, pages: [
    { title: "Overview", path: "/", icon: LayoutDashboard },
    { title: "User Analytics", path: "/users", icon: Users },
    { title: "Model & LLM Usage", path: "/usage/models", icon: Cpu },
    { title: "Revenue & Costs", path: "/revenue", icon: DollarSign },
    { title: "Service Usage", path: "/services", icon: Activity },
    { title: "Calls & SMS", path: "/comms", icon: Phone },
    { title: "Session Recordings", path: "/analytics", icon: MonitorPlay },
    { title: "Heatmap", path: "/analytics/heatmap", icon: Flame },
    { title: "Research SLOs", path: "/research-analytics", icon: BarChart3 },
    { title: "SEO & Search", path: "/seo", icon: Search },
    { title: "Financials", path: "/financials", icon: TrendingUp },
    { title: "Shop Revenue", path: "/shop-revenue", icon: ShoppingBag },
    { title: "Acquisition", path: "/acquisition", icon: UserSearch },
    { title: "Beta Waitlist", path: "/beta-waitlist", icon: Mail },
    { title: "Employees", path: "/employees", icon: Users },
    { title: "Pricing", path: "/pricing", icon: Tag },
    { title: "Discounts", path: "/discounts", icon: Ticket },
    { title: "Library", path: "/library", icon: BookOpen },
    { title: "Library Earnings", path: "/library/earnings", icon: DollarSign },
    { title: "Library Buyers", path: "/library/buyers", icon: Users },
    { title: "Platform Ledger", path: "/library/platform-ledger", icon: Activity },
    { title: "System Health", path: "/system", icon: Server },
    { title: "Indexer Fleet & Pool", path: "/indexers", icon: Server },
    { title: "Model Map", path: "/model-map", icon: Network },
    { title: "Audit Log", path: "/audit", icon: FileText },
    { title: "Access Control", path: "/admin", icon: Shield },
    { title: "Compliance", path: "/compliance", icon: ShieldCheck },
    { title: "Company Info", path: "/company", icon: Briefcase },
  ] },
  // Mirrors ACCOUNT_ITEMS in components/app-sidebar.tsx.
  { key: "account.askfinz.ai", name: "Account", icon: BadgeCheck, pages: [
    { title: "Overview", path: "/account", icon: LayoutDashboard },
    { title: "Profile", path: "/profile", icon: User },
    { title: "Security & Access", path: "/security", icon: Shield },
    { title: "Notifications", path: "/notifications", icon: Bell },
    { title: "Billing", path: "/billing", icon: CreditCard },
    { title: "Usage", path: "/usage", icon: Activity },
    { title: "Credits", path: "/credits", icon: Coins },
    { title: "API Keys", path: "/api-keys", icon: Lock },
    { title: "Integrations", path: "/integrations", icon: Server },
    { title: "Teams", path: "/teams", icon: Users },
    { title: "Company", path: "/company", icon: Briefcase },
  ] },
]

function buildMoreApp(def: (typeof MORE_DEFS)[number], origin: string, pathname: string, token: string | null, activeHost?: string): SbApp {
  const baseUrl = getAppBaseUrl(def.key)
  const active = activeHost ? def.key === activeHost : (origin !== "" && origin === baseUrl)
  return {
    key: def.key, name: def.name, icon: def.icon, active, badge: def.badge,
    pages: def.pages.map((p) => ({
      title: p.title, path: p.path, icon: p.icon, url: pageHref(def.key, p.path, undefined, origin, token),
      active: active && (p.path === "/" ? pathname === "/" : (pathname === p.path || (p.path !== "/" && pathname.startsWith(p.path)))),
    })),
  }
}

function pageHref(appKey: string, path: string, crossAppKey: string | undefined, origin: string, token: string | null): string {
  const itemBase = crossAppKey ? getAppBaseUrl(crossAppKey) : getAppBaseUrl(appKey)
  const sameDomain = origin !== "" && origin === itemBase
  if (sameDomain) return path.startsWith("/") ? path : `/${path}`
  const fullPath = `${itemBase}${path.startsWith("/") ? path : `/${path}`}`
  return (origin && origin !== itemBase && token) ? `${fullPath}${fullPath.includes("?") ? "&" : "?"}token=${encodeURIComponent(token)}` : fullPath
}

function buildSbApp(
  app: (typeof GLOBAL_APPS)[number], origin: string, pathname: string, token: string | null, activeHost?: string,
): SbApp {
  const baseUrl = getAppBaseUrl(app.key)
  const active = activeHost ? app.key === activeHost : (origin !== "" && origin === baseUrl)
  return {
    key: app.key, name: app.name, icon: app.icon, active,
    pages: app.pages.map((p) => ({
      title: p.title, path: p.path, icon: p.icon,
      url: pageHref(app.key, p.path, p.crossAppKey, origin, token),
      active: active && (p.path === "/" ? pathname === "/" : (pathname === p.path || (p.path !== "/" && pathname.startsWith(p.path)))),
      subs: buildSubs(app.key, p.path, origin, token),
    })),
  }
}

function sbAppByKey(key: string, origin: string, pathname: string, token: string | null, activeHost?: string): SbApp | null {
  const app = GLOBAL_APPS.find((a) => a.key === key)
  return app ? buildSbApp(app, origin, pathname, token, activeHost) : null
}

/**
 * Resolve the current app + page from the browser origin + pathname, for the
 * header breadcrumb. Falls back to empty host (the breadcrumb then shows the
 * caller-supplied overrides).
 */
export function getCurrentAppPage(
  origin: string, pathname: string,
): { host: string; appName: string; pageTitle: string; icon: LucideIcon } {
  const o = (origin || "").replace(/\/$/, "")
  const pick = (pages: { title: string; path: string; icon: LucideIcon }[]) => {
    let page = pages.find((p) => p.path !== "/" && (pathname === p.path || pathname.startsWith(p.path)))
    if (!page) page = pages.find((p) => p.path === "/" && pathname === "/")
    return page
  }
  // Skip the hidden "Comms" alias (shares dash.askfinz.ai with askFinz Portal);
  // it lives inside the Portal now, so dash should resolve to the Portal below.
  const app = GLOBAL_APPS.find((a) => a.name !== "Comms" && getAppBaseUrl(a.key) === o)
  if (app) {
    const page = pick(app.pages)
    return { host: app.key, appName: app.name, pageTitle: page?.title ?? "", icon: page?.icon ?? app.icon }
  }
  // Fall back to the "More" apps (Account / askFinz Portal / Feedback / Dev Docs)
  // which are real subdomains but aren't part of GLOBAL_APPS.
  const more = MORE_DEFS.find((d) => getAppBaseUrl(d.key) === o)
  if (more) {
    const page = pick(more.pages)
    return { host: more.key, appName: more.name, pageTitle: page?.title ?? "", icon: page?.icon ?? more.icon }
  }
  // Last resort: show the bare host so the breadcrumb is never blank.
  return { host: o.replace(/^https?:\/\//, ""), appName: "", pageTitle: "", icon: Home }
}

// ── localStorage helpers (client-only, fail-safe) ────────────────────────────
function lsGet<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback
  try { const v = localStorage.getItem(key); return v == null ? fallback : (JSON.parse(v) as T) } catch { return fallback }
}
function lsSet(key: string, val: unknown) {
  if (typeof window === "undefined") return
  try { localStorage.setItem(key, JSON.stringify(val)) } catch { /* ignore */ }
}

const PIN_KEY = "AskFinz_nav_pinned"
const OPEN_KEY = "AskFinz_nav_open"
const GROUPS_KEY = "AskFinz_nav_groups"
const RECENTS_KEY = "AskFinz_nav_recents"
const SCROLL_KEY = "AskFinz_nav_scroll"
// Sidebar collapsed/expanded — a cross-subdomain cookie (domain=.askfinz.ai) so
// the state carries across every webApp, not just within one subdomain's localStorage.
const RAIL_COOKIE = "AskFinz_nav_rail_state"
export function readRailCollapsed(): boolean { return getSharedCookie(RAIL_COOKIE) === "collapsed" }
export function writeRailCollapsed(collapsed: boolean) { setSharedCookie(RAIL_COOKIE, collapsed ? "collapsed" : "expanded", 365) }

const DEFAULT_PINNED = ["chat.askfinz.ai", "search.askfinz.ai", "mail.askfinz.ai"]
type RecentRec = { host: string; path: string; title: string }

export interface GlobalSidebarProps {
  /** Override the active app host (e.g. for apps not in GLOBAL_APPS, like template). */
  activeHost?: string
}

// ── The radial Navigation Wheel ──────────────────────────────────────────────
function NavWheel({ apps, onGo, onClose }: { apps: SbApp[]; onGo: (url: string) => void; onClose: () => void }) {
  const [wApp, setWApp] = useState(0)
  const [wPage, setWPage] = useState(-1)
  const [wSub, setWSub] = useState(-1)
  const [query, setQuery] = useState("")
  const [size, setSize] = useState(680)

  useEffect(() => {
    const calc = () => setSize(Math.max(560, Math.min(900, Math.min(window.innerWidth, window.innerHeight) - 64)))
    calc(); window.addEventListener("resize", calc)
    return () => window.removeEventListener("resize", calc)
  }, [])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return apps
    const r = apps.filter((a) => a.name.toLowerCase().includes(q) || a.pages.some((p) => p.title.toLowerCase().includes(q)))
    return r.length ? r : apps
  }, [apps, query])

  const N = Math.max(filtered.length, 1)
  const ai = Math.min(Math.max(wApp, 0), N - 1)
  const focus = filtered[ai]
  const pages = focus ? focus.pages : []
  const M = Math.max(pages.length, 1)
  const focusPage = wPage >= 0 && focus ? pages[wPage] : null
  const subs = focusPage?.subs ?? []
  const S = Math.max(subs.length, 1)

  const goWheel = useCallback(() => {
    if (!focus) return
    const pj = wPage >= 0 ? wPage : 0
    const page = focus.pages[pj]
    if (!page) return
    if (wSub >= 0 && page.subs && page.subs[wSub]) onGo(page.subs[wSub].url)
    else onGo(page.url)
  }, [focus, wPage, wSub, onGo])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.preventDefault(); onClose(); return }
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        e.preventDefault(); const d = e.key === "ArrowRight" ? 1 : -1
        setWApp((a) => ((Math.min(a, N - 1)) + d + N) % N); setWPage(-1); setWSub(-1); return
      }
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        e.preventDefault(); const d = e.key === "ArrowDown" ? 1 : -1
        setWPage((p) => ((p < 0 ? (d > 0 ? -1 : 0) : p) + d + M) % M); setWSub(-1); return
      }
      if (e.key === "[" || e.key === "]") {
        if (subs.length < 1) return
        e.preventDefault(); const d = e.key === "]" ? 1 : -1
        setWSub((sIdx) => ((sIdx < 0 ? (d > 0 ? -1 : 0) : sIdx) + d + subs.length) % subs.length); return
      }
      if (e.key === "Enter") { e.preventDefault(); goWheel(); return }
      if (e.key === "Backspace") { e.preventDefault(); setQuery((q) => q.slice(0, -1)); setWApp(0); setWPage(-1); setWSub(-1); return }
      if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault(); setQuery((q) => q + e.key); setWApp(0); setWPage(-1); setWSub(-1)
      }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [N, M, subs.length, goWheel, onClose])

  const k = size / 720
  const cx = size / 2, cy = size / 2
  const hubR = 70 * k, appR = 116 * k, appBandIn = 86 * k, appBandOut = 150 * k, appSz = 38 * k, appFocusSz = 52 * k
  const pageR = 200 * k, pageBandIn = 154 * k, pageBandOut = 246 * k, pageSz = 34 * k, pageFocusSz = 46 * k
  const subR = 292 * k, subBandIn = 252 * k, subBandOut = 332 * k
  const outerR = 332 * k
  const div = "var(--afn-border)"
  const donut = (r1: number, r2: number) =>
    `M ${cx} ${cy - r2} A ${r2} ${r2} 0 1 1 ${cx - 0.01} ${cy - r2} Z M ${cx} ${cy - r1} A ${r1} ${r1} 0 1 1 ${cx - 0.01} ${cy - r1} Z`
  const annular = (r1: number, r2: number, a0: number, a1: number) => {
    const rad = (a: number) => a * Math.PI / 180
    const P = (r: number, a: number) => [cx + r * Math.cos(rad(a)), cy + r * Math.sin(rad(a))]
    const large = Math.abs(a1 - a0) > 180 ? 1 : 0
    const p1 = P(r2, a0), p2 = P(r2, a1), p3 = P(r1, a1), p4 = P(r1, a0)
    return `M ${p1[0]} ${p1[1]} A ${r2} ${r2} 0 ${large} 1 ${p2[0]} ${p2[1]} L ${p3[0]} ${p3[1]} A ${r1} ${r1} 0 ${large} 0 ${p4[0]} ${p4[1]} Z`
  }

  const onMove = (ev: React.MouseEvent) => {
    const rect = ev.currentTarget.getBoundingClientRect()
    const mx = rect.left + rect.width / 2, my = rect.top + rect.height / 2
    const dx = ev.clientX - mx, dy = ev.clientY - my
    const dist = Math.hypot(dx, dy)
    if (dist < hubR) return
    const a = (Math.atan2(dy, dx) * 180 / Math.PI + 90 + 360) % 360
    if (dist <= appBandOut) {
      const i = Math.round(a / (360 / N)) % N
      if (i !== ai) { setWApp(i); setWPage(-1) }
      return
    }
    if (dist <= pageBandOut) {
      const j = Math.round(a / (360 / M)) % M
      if (j !== wPage) { setWPage(j); setWSub(-1) }
      return
    }
    // Outer ring — sub-pages of the focused page
    const pj = Math.min(wPage < 0 ? 0 : wPage, M - 1)
    const pg = pages[pj]
    const slist = pg?.subs ?? []
    if (!slist.length) { if (wPage !== pj) setWPage(pj); return }
    const kk = Math.round(a / (360 / slist.length)) % slist.length
    if (wPage !== pj || wSub !== kk) { setWPage(pj); setWSub(kk) }
  }

  const chip = (key: string, x: number, y: number, sz: number, on: boolean, Ico: LucideIcon, isz: number, onClick: () => void, onEnter: () => void) => (
    <div key={key} onClick={onClick} onMouseEnter={onEnter} style={{
      position: "absolute", left: x, top: y, transform: "translate(-50%,-50%)", width: sz, height: sz, borderRadius: "50%",
      display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
      background: on ? "var(--afn-accent)" : "var(--afn-card)", color: on ? "var(--afn-accent-fg)" : "var(--afn-fg)",
      border: "1px solid " + (on ? "var(--afn-accent)" : "var(--afn-border)"),
      boxShadow: on ? "0 10px 28px rgba(0,0,0,.28)" : "var(--shadow-sm, 0 1px 2px rgba(0,0,0,.08))",
      transition: "all .14s cubic-bezier(.16,1,.3,1)", zIndex: on ? 6 : 2,
    }}><Ico size={isz} /></div>
  )

  if (typeof document === "undefined") return null
  return createPortal(
    <div className="afn-shell">
      <style>{AFN_CSS}</style>
      <div onClick={onClose} style={{
        position: "fixed", inset: 0, zIndex: 2147483600, display: "flex", alignItems: "center", justifyContent: "center",
        background: "color-mix(in oklab, var(--afn-bg) 52%, transparent)", backdropFilter: "blur(7px)", WebkitBackdropFilter: "blur(7px)",
        animation: "afnWheelIn .18s cubic-bezier(.16,1,.3,1)",
      }}>
      <div onClick={(e) => e.stopPropagation()} onMouseMove={onMove} style={{ position: "relative", width: size, height: size, flex: "none" }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ position: "absolute", inset: 0 }}>
          <path d={donut(appBandIn, appBandOut)} fill="color-mix(in oklab, var(--afn-fg) 4%, transparent)" fillRule="evenodd" />
          <path d={donut(pageBandIn, pageBandOut)} fill="color-mix(in oklab, var(--afn-fg) 3%, transparent)" fillRule="evenodd" />
          {subs.length > 0 && <path d={donut(subBandIn, subBandOut)} fill="color-mix(in oklab, var(--afn-fg) 2%, transparent)" fillRule="evenodd" />}
          {focus && (() => { const a = -90 + 360 * ai / N; return <path d={annular(appBandIn, appBandOut, a - 180 / N, a + 180 / N)} fill="color-mix(in oklab, var(--afn-accent) 14%, transparent)" /> })()}
          {wPage >= 0 && focus && (() => { const a = -90 + 360 * wPage / M; return <path d={annular(pageBandIn, pageBandOut, a - 180 / M, a + 180 / M)} fill={`color-mix(in oklab, var(--afn-accent) ${wSub >= 0 ? 10 : 16}%, transparent)`} /> })()}
          {wSub >= 0 && subs.length > 0 && (() => { const a = -90 + 360 * wSub / S; return <path d={annular(subBandIn, subBandOut, a - 180 / S, a + 180 / S)} fill="color-mix(in oklab, var(--afn-accent) 18%, transparent)" /> })()}
          {Array.from({ length: N }).map((_, i) => { const a = (-90 + 360 * (i + 0.5) / N) * Math.PI / 180; return <line key={"da" + i} x1={cx + appBandIn * Math.cos(a)} y1={cy + appBandIn * Math.sin(a)} x2={cx + appBandOut * Math.cos(a)} y2={cy + appBandOut * Math.sin(a)} stroke={div} strokeWidth={1} /> })}
          {focus && Array.from({ length: M }).map((_, j) => { const a = (-90 + 360 * (j + 0.5) / M) * Math.PI / 180; return <line key={"dp" + j} x1={cx + pageBandIn * Math.cos(a)} y1={cy + pageBandIn * Math.sin(a)} x2={cx + pageBandOut * Math.cos(a)} y2={cy + pageBandOut * Math.sin(a)} stroke={div} strokeWidth={1} /> })}
          <circle cx={cx} cy={cy} r={hubR} fill="var(--afn-card)" stroke={div} strokeWidth={1} />
          <circle cx={cx} cy={cy} r={appBandOut} fill="none" stroke={div} strokeWidth={1} />
          <circle cx={cx} cy={cy} r={pageBandOut} fill="none" stroke={div} strokeWidth={1} />
          <circle cx={cx} cy={cy} r={outerR} fill="none" stroke={div} strokeWidth={1} />
        </svg>
        {filtered.map((app, i) => { const a = (-90 + 360 * i / N) * Math.PI / 180; const f = i === ai; return chip(app.key, cx + appR * Math.cos(a), cy + appR * Math.sin(a), f ? appFocusSz : appSz, f, app.icon, f ? 21 : 16, () => onGo(app.pages[0]?.url ?? "#"), () => { setWApp(i); setWPage(-1) }) })}
        {focus && pages.map((p, j) => { const a = (-90 + 360 * j / M) * Math.PI / 180; const f = j === wPage; return chip("pg" + j, cx + pageR * Math.cos(a), cy + pageR * Math.sin(a), f ? pageFocusSz : pageSz, f, p.icon, f ? 19 : 15, () => onGo(p.url), () => setWPage(j)) })}
        {focusPage && subs.map((sp, j) => {
          const a = (-90 + 360 * j / S) * Math.PI / 180
          const x = cx + subR * Math.cos(a), y = cy + subR * Math.sin(a)
          const right = Math.cos(a) >= -0.01
          const f = j === wSub
          const SIco = sp.icon
          return (
            <div key={"sp" + j} onClick={(e) => { e.stopPropagation(); onGo(sp.url) }} onMouseEnter={() => setWSub(j)} style={{ position: "absolute", left: x, top: y, transform: "translate(-50%,-50%)", display: "flex", alignItems: "center", gap: 6, flexDirection: right ? "row" : "row-reverse", cursor: "pointer", maxWidth: 116, zIndex: 5 }}>
              <span style={{ display: "flex", flex: "none", color: f ? "var(--afn-accent)" : "var(--afn-muted-fg)" }}><SIco size={13} /></span>
              <span style={{ font: (f ? "600" : "500") + " 12px var(--afn-font-sans)", whiteSpace: "nowrap", color: f ? "var(--afn-fg)" : "var(--afn-muted-fg)", textShadow: "0 1px 4px var(--afn-bg), 0 0 4px var(--afn-bg)" }}>{sp.title}</span>
            </div>
          )
        })}
        <div style={{ position: "absolute", left: cx, top: cy, transform: "translate(-50%,-50%)", width: hubR * 2, textAlign: "center", pointerEvents: "none" }}>
          <div style={{ font: "700 8px var(--afn-font-mono)", letterSpacing: ".12em", textTransform: "uppercase", color: "var(--afn-muted-fg)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{focus ? focus.key : "no results"}</div>
          <div style={{ font: "600 17px var(--afn-font-serif)", color: "var(--afn-fg)", margin: "2px 0 1px", lineHeight: 1.1 }}>{focus ? focus.name : "—"}</div>
          <div style={{ font: (focusPage ? "600" : "500") + " 11px var(--afn-font-sans)", color: focusPage ? "var(--afn-fg)" : "var(--afn-muted-fg)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{focusPage ? focusPage.title : (query ? `“${query}”` : "Choose a page")}</div>
          {focusPage && wSub >= 0 && subs[wSub]
            ? <div style={{ font: "500 10.5px var(--afn-font-sans)", color: "var(--afn-accent)", marginTop: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>&rarr; {subs[wSub].title}</div>
            : (focusPage && subs.length > 0 ? <div style={{ font: "500 9px var(--afn-font-mono)", color: "var(--afn-muted-fg)", marginTop: 2, letterSpacing: ".04em" }}>{subs.length} SUB-PAGES</div> : null)}
        </div>
      </div>
      <div style={{ position: "absolute", bottom: 28, left: 0, right: 0, textAlign: "center", font: "500 12px var(--afn-font-sans)", color: "var(--afn-muted-fg)", pointerEvents: "none" }}>Point at a slice, or use the keyboard &mdash;&nbsp; &larr; &rarr;&nbsp; switch app &nbsp;&middot;&nbsp; &uarr; &darr;&nbsp; choose page &nbsp;&middot;&nbsp; [ ]&nbsp; sub-pages &nbsp;&middot;&nbsp; type to filter &nbsp;&middot;&nbsp; &crarr; go &nbsp;&middot;&nbsp; Esc close</div>
      <button onClick={(e) => { e.stopPropagation(); onClose() }} style={{ position: "absolute", top: 26, right: 30, width: 36, height: 36, borderRadius: 9, border: "1px solid var(--afn-border)", background: "var(--afn-card)", color: "var(--afn-fg)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><X size={17} /></button>
      </div>
    </div>,
    document.body,
  )
}

// ── Command palette (Ctrl/⌘ K) ───────────────────────────────────────────────
type CmdRow = { header?: string; empty?: boolean; icon?: LucideIcon; label?: string; sub?: string; url?: string }
function CommandPalette({ apps, pinned, recents, onClose }: { apps: SbApp[]; pinned: string[]; recents: SbApp[]; onClose: () => void }) {
  const [q, setQ] = useState("")
  const [idx, setIdx] = useState(0)

  const { rows, items } = useMemo(() => {
    const rows: CmdRow[] = []; const items: CmdRow[] = []
    const push = (it: CmdRow) => { items.push(it); rows.push(it) }
    const query = q.trim().toLowerCase()
    if (!query) {
      if (recents.length) { rows.push({ header: "Recent" }); recents.slice(0, 4).forEach((a) => push({ icon: a.icon, label: a.pages[0]?.title ?? a.name, sub: a.name, url: a.pages[0]?.url })) }
      const pins = pinned.map((h) => apps.find((a) => a.key === h)).filter(Boolean) as SbApp[]
      if (pins.length) { rows.push({ header: "Pinned" }); pins.forEach((a) => push({ icon: a.icon, label: a.name, sub: "App", url: a.pages[0]?.url })) }
      rows.push({ header: "All apps" })
      apps.forEach((a) => push({ icon: a.icon, label: a.name, sub: a.key, url: a.pages[0]?.url }))
      return { rows, items }
    }
    const appHits: CmdRow[] = [], pageHits: CmdRow[] = []
    apps.forEach((a) => {
      if (a.name.toLowerCase().includes(query)) appHits.push({ icon: a.icon, label: a.name, sub: a.key, url: a.pages[0]?.url })
      a.pages.forEach((p) => { if (p.title.toLowerCase().includes(query) || a.name.toLowerCase().includes(query)) pageHits.push({ icon: p.icon, label: p.title, sub: a.name, url: p.url }) })
    })
    if (appHits.length) { rows.push({ header: "Apps" }); appHits.forEach(push) }
    if (pageHits.length) { rows.push({ header: "Pages" }); pageHits.slice(0, 30).forEach(push) }
    if (!appHits.length && !pageHits.length) rows.push({ empty: true })
    return { rows, items }
  }, [q, apps, pinned, recents])

  const sel = Math.min(idx, Math.max(items.length - 1, 0))
  const run = useCallback((it?: CmdRow) => { if (it?.url) { window.location.href = it.url } }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const N = items.length
      if (e.key === "Escape") { e.preventDefault(); onClose(); return }
      if (e.key === "ArrowDown") { e.preventDefault(); setIdx((i) => N ? (i + 1) % N : 0); return }
      if (e.key === "ArrowUp") { e.preventDefault(); setIdx((i) => N ? (i - 1 + N) % N : 0); return }
      if (e.key === "Enter") { e.preventDefault(); run(items[Math.min(sel, N - 1)]); return }
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [items, sel, run, onClose])

  let runningIdx = -1
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 1010, display: "flex", alignItems: "flex-start", justifyContent: "center", paddingTop: "14vh", background: "color-mix(in oklab, var(--afn-bg) 50%, transparent)", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)", animation: "afnWheelIn .16s cubic-bezier(.16,1,.3,1)" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "min(92vw, 580px)", maxHeight: "64vh", display: "flex", flexDirection: "column", background: "var(--afn-card)", border: "1px solid var(--afn-border)", borderRadius: 14, boxShadow: "var(--shadow-pop, 0 24px 60px rgba(0,0,0,.34))", overflow: "hidden" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "14px 16px", borderBottom: "1px solid var(--afn-border)" }}>
          <span style={{ display: "flex", flex: "none", color: "var(--afn-muted-fg)" }}><Search size={17} /></span>
          <input autoFocus value={q} onChange={(e) => { setQ(e.target.value); setIdx(0) }} placeholder="Search apps & pages…" style={{ flex: 1, border: "none", outline: "none", background: "transparent", color: "var(--afn-fg)", font: "500 15px var(--afn-font-sans)" }} />
          <span style={{ font: "600 10px var(--afn-font-mono)", padding: "3px 7px", borderRadius: 6, background: "var(--afn-muted)", color: "var(--afn-muted-fg)" }}>ESC</span>
        </div>
        <div className="afn-sb-scroll" style={{ overflowY: "auto", padding: "4px 0 8px" }}>
          {rows.map((r, i) => {
            if (r.header) return <div key={"h" + i} style={{ padding: "12px 14px 5px", font: "700 9px var(--afn-font-mono)", letterSpacing: ".12em", textTransform: "uppercase", color: "var(--afn-muted-fg)" }}>{r.header}</div>
            if (r.empty) return <div key={"e" + i} style={{ padding: "34px 14px", textAlign: "center", font: "500 13px var(--afn-font-sans)", color: "var(--afn-muted-fg)" }}>No results.</div>
            runningIdx += 1; const my = runningIdx; const on = my === sel; const Ico = r.icon ?? Search
            return (
              <div key={"i" + i} onMouseEnter={() => setIdx(my)} onClick={() => run(r)} style={{ margin: "0 8px", padding: "8px 10px", borderRadius: 7, display: "flex", alignItems: "center", gap: 10, cursor: "pointer", background: on ? "var(--afn-muted)" : "transparent" }}>
                <span style={{ display: "flex", flex: "none", color: on ? "var(--afn-fg)" : "var(--afn-muted-fg)" }}><Ico size={16} /></span>
                <span style={{ flex: 1, font: "500 13.5px var(--afn-font-sans)", color: "var(--afn-fg)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.label}</span>
                <span style={{ font: "600 9px var(--afn-font-mono)", letterSpacing: ".06em", textTransform: "uppercase", color: "var(--afn-muted-fg)", whiteSpace: "nowrap" }}>{r.sub}</span>
                {on ? <span style={{ font: "600 10px var(--afn-font-mono)", padding: "2px 6px", borderRadius: 5, background: "var(--afn-card)", border: "1px solid var(--afn-border)", color: "var(--afn-muted-fg)" }}>&crarr;</span> : null}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/**
 * GlobalSidebar — the full redesigned sidebar (expanded + collapsed rail) plus
 * the navigation wheel (Ctrl/⌘ + Space) and command palette (Ctrl/⌘ + K).
 * Self-contained; drop it next to your page content inside a flex row.
 */
export function GlobalSidebar({ activeHost }: GlobalSidebarProps) {
  const pathname = usePathname() ?? "/"
  const [origin, setOrigin] = useState("")
  const [admin, setAdmin] = useState(false)
  const [rail, setRail] = useState(false)
  const [pinned, setPinned] = useState<string[]>(DEFAULT_PINNED)
  const [openApps, setOpenApps] = useState<string[]>([])
  const [groups, setGroups] = useState<Record<string, boolean>>({ Core: true, Tools: true, Verticals: true })
  const [recents, setRecents] = useState<RecentRec[]>([])
  const [wheelOpen, setWheelOpen] = useState(false)
  const [user, setUser] = useState<{ name: string; email: string; avatar: string } | undefined>(undefined)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [teamMenuOpen, setTeamMenuOpen] = useState(false)
  const token = useRef<string | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  // Mobile: below 768px the sidebar becomes an off-canvas drawer instead of a
  // permanent column. The header's panel button toggles `mobileOpen` (see the
  // afn-nav:toggle-rail handler). A ref keeps the latest value available inside
  // the stable event-listener closures below.
  const isMobile = useIsMobile()
  const [mobileOpen, setMobileOpen] = useState(false)
  const isMobileRef = useRef(isMobile)
  isMobileRef.current = isMobile

  // Restore the sidebar's last scroll position after the nav renders (it is
  // lost on every full-page navigation since the shell remounts). Saved per-tab
  // in sessionStorage on scroll.
  useEffect(() => {
    if (!origin) return
    const el = scrollRef.current
    if (!el) return
    const raf = requestAnimationFrame(() => {
      try { const v = sessionStorage.getItem(SCROLL_KEY); if (v != null) el.scrollTop = Number(v) || 0 } catch { /* ignore */ }
    })
    return () => cancelAnimationFrame(raf)
  }, [origin])

  // Resolve client-only state after mount (SSR-safe; avoids React #418).
  useEffect(() => {
    setOrigin(window.location.origin)
    const raw = readAuthToken()
    token.current = raw && raw.split(".").length === 3 && raw.startsWith("eyJ") ? raw : null
    const u = getSidebarUser(); if (u) setUser({ name: u.name, email: u.email, avatar: u.avatar })
    const checkAdmin = () => { const su = getSidebarUser(); setAdmin(!!su?.email && ["paul@pvarghese.com"].includes(su.email.toLowerCase())) }
    checkAdmin()
    setPinned(lsGet(PIN_KEY, DEFAULT_PINNED))
    setOpenApps(lsGet(OPEN_KEY, [] as string[]))
    setGroups(lsGet(GROUPS_KEY, { Core: true, Tools: true, Verticals: true }))
    setRail(readRailCollapsed())
    setRecents(lsGet(RECENTS_KEY, [] as RecentRec[]))
    window.addEventListener("authUserUpdated", checkAdmin)
    return () => window.removeEventListener("authUserUpdated", checkAdmin)
  }, [])

  // Auto-open the active app's section once origin is known.
  useEffect(() => {
    if (!origin) return
    const cur = activeHost || (GLOBAL_APPS.find((a) => getAppBaseUrl(a.key) === origin)?.key)
    if (cur) setOpenApps((o) => (o.includes(cur) ? o : o.concat(cur)))
  }, [origin, activeHost])

  // Open the shared command palette (SearchDialog owns Ctrl/⌘+K). We trigger it
  // by re-dispatching that shortcut so the design's own search buttons work too.
  const openSearch = useCallback(() => {
    if (typeof document !== "undefined") document.dispatchEvent(new KeyboardEvent("keydown", { key: "k", ctrlKey: true, bubbles: true }))
  }, [])

  // Keyboard: Ctrl/⌘+Space → wheel (Ctrl/⌘+K is handled by SearchDialog). Plus
  // window events so the header/breadcrumb buttons can drive the same actions.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey
      if (mod && e.code === "Space") { e.preventDefault(); setWheelOpen((v) => !v) }
      if (e.key === "Escape") setMobileOpen(false)
    }
    // On mobile the same panel button opens/closes the off-canvas drawer; on
    // desktop it collapses/expands the rail as before.
    const onToggleRail = () => {
      if (isMobileRef.current) setMobileOpen((v) => !v)
      else setRail((v) => { const n = !v; writeRailCollapsed(n); return n })
    }
    const onOpenWheel = () => setWheelOpen(true)
    const onOpenCmd = () => openSearch()
    window.addEventListener("keydown", onKey)
    window.addEventListener("afn-nav:toggle-rail", onToggleRail)
    window.addEventListener("afn-nav:open-wheel", onOpenWheel)
    window.addEventListener("afn-nav:open-command", onOpenCmd)
    return () => {
      window.removeEventListener("keydown", onKey)
      window.removeEventListener("afn-nav:toggle-rail", onToggleRail)
      window.removeEventListener("afn-nav:open-wheel", onOpenWheel)
      window.removeEventListener("afn-nav:open-command", onOpenCmd)
    }
  }, [openSearch])

  // Resizing from phone → desktop must not leave a half-open drawer behind.
  useEffect(() => { if (!isMobile) setMobileOpen(false) }, [isMobile])

  const model = useMemo(() => {
    // "Comms" lives inside askFinz Portal (Calls & SMS), not as its own entry.
    const visible = GLOBAL_APPS.filter((a) => (!a.hidden || admin) && a.name !== "Comms")
    const byCat = (cat: SidebarCategory) => visible.filter((a) => appCategory(a.name) === cat)
      .sort((a, b) => (APP_ORDER[a.name] ?? 999) - (APP_ORDER[b.name] ?? 999))
      .map((a) => buildSbApp(a, origin, pathname, token.current, activeHost))
    return {
      home: byCat("home")[0] || null,
      groups: [
        { label: "Core" as const, apps: byCat("core") },
        { label: "Tools" as const, apps: byCat("tools") },
        { label: "Verticals" as const, apps: byCat("vertical") },
      ],
    }
  }, [origin, pathname, admin, activeHost])

  const moreApps = useMemo(() => MORE_DEFS.filter((d) => !d.adminOnly || admin).map((d) => buildMoreApp(d, origin, pathname, token.current, activeHost)), [origin, pathname, activeHost, admin])
  const groupApps = useMemo(() => [model.home, ...model.groups.flatMap((g) => g.apps)].filter(Boolean) as SbApp[], [model])
  const allApps = useMemo(() => [...groupApps, ...moreApps], [groupApps, moreApps])
  const pinnedApps = useMemo(() => pinned.map((h) => allApps.find((a) => a.key === h)).filter(Boolean) as SbApp[], [pinned, allApps])
  const recentApps = useMemo(() => recents.map((r) => { const a = allApps.find((x) => x.key === r.host); return a ? { ...a, pages: [{ title: r.title, url: pageHref(r.host, r.path, undefined, origin, token.current), icon: a.icon, active: false, path: r.path }] } : null }).filter(Boolean) as SbApp[], [recents, allApps, origin])
  const searchItems = useMemo(() => getGlobalNavSearchEntries() as unknown as SearchItem[], [origin, admin])

  // On the finance terminal, fold live ticker search into the global ⌘K search
  // (replaces the removed in-app search bar). Gated to finance so every other
  // app keeps the default page-only filtering untouched.
  const financeSearch = useMemo<((q: string) => Promise<SearchItem[]>) | undefined>(() => {
    if (typeof window === "undefined") return undefined
    const onFinance = window.location.hostname.includes("finance") || window.location.port === "9507"
    if (!onFinance) return undefined
    return async (q: string): Promise<SearchItem[]> => {
      const ql = q.toLowerCase()
      const nav = searchItems.filter((it) => `${it.title} ${it.description || ""} ${it.category || ""}`.toLowerCase().includes(ql))
      // Which finance surface to open a ticker into (the security hub sub-pages).
      const TICKER_PAGES = [
        { sub: "", label: "Stock Detail" },
        { sub: "chart", label: "Advanced Chart" },
        { sub: "options", label: "Options Chain" },
        { sub: "depth", label: "Level 2 · Tape" },
        { sub: "relations", label: "Relationship Map" },
        { sub: "compare", label: "Comparison" },
      ]
      let tickers: SearchItem[] = []
      try {
        const r = await fetch(`/api/mkt/search?q=${encodeURIComponent(q)}`)
        if (r.ok) {
          const j = await r.json()
          const rows: { t: string; name: string; exch?: string; type?: string; logo?: string }[] = (j.results || []).slice(0, 8)
          // Company logo before each ticker row (falls back: hidden on 404).
          const tickerIcon = (t: { t: string; logo?: string }) => (
            <img
              src={t.logo || `/api/mkt/logo?symbol=${encodeURIComponent(t.t)}&size=36`}
              alt="" width={18} height={18} loading="lazy"
              style={{ borderRadius: 4, objectFit: "contain", background: "#fff", flex: "0 0 auto" }}
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none" }}
            />
          )
          // Expand the best (exact, else first) match into "open in <page>" rows so
          // the user picks the destination; other matches collapse to one row each.
          const exactIdx = rows.findIndex((t) => t.t.toLowerCase() === ql)
          const primaryIdx = exactIdx >= 0 ? exactIdx : 0
          tickers = rows.flatMap((t, idx) => {
            if (idx === primaryIdx) {
              return TICKER_PAGES.map((p) => ({
                id: `tk-${t.t}-${p.sub || "detail"}`,
                title: `Open in ${p.label}`,
                description: `${t.name}${t.exch ? " · " + t.exch : ""}`,
                url: `/stock/${t.t}${p.sub ? "/" + p.sub : ""}`,
                category: `${t.t} · ${t.name}`,
                icon: tickerIcon(t),
              }))
            }
            return [{
              id: `tk-${t.t}`, title: `${t.t} · ${t.name}`,
              description: (t.exch || t.type || "Ticker"), url: `/stock/${t.t}`, category: "More tickers",
              icon: tickerIcon(t),
            }]
          })
        }
      } catch { /* ignore — fall back to page results */ }
      return [...tickers, ...nav]
    }
  }, [searchItems])

  const togglePin = (key: string) => setPinned((p) => { const n = p.includes(key) ? p.filter((x) => x !== key) : p.concat(key); lsSet(PIN_KEY, n); return n })
  const toggleApp = (key: string) => setOpenApps((o) => { const n = o.includes(key) ? o.filter((x) => x !== key) : o.concat(key); lsSet(OPEN_KEY, n); return n })
  const toggleGroup = (label: string) => setGroups((g) => { const n = { ...g, [label]: g[label] === false }; lsSet(GROUPS_KEY, n); return n })
  const recordRecent = (host: string, path: string, title: string) => setRecents((r) => { const n = [{ host, path, title }, ...r.filter((x) => !(x.host === host && x.path === path))].slice(0, 5); lsSet(RECENTS_KEY, n); return n })
  const navTo = (url: string) => { setWheelOpen(false); if (typeof window !== "undefined") window.location.href = url }

  const initials = (user?.name || "U").split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase()

  // ── Pinned + recents + groups list ─────────────────────────────────────────
  const renderAppRow = (a: SbApp) => {
    const open = openApps.includes(a.key)
    const Ico = a.icon
    return (
      <div key={a.key} style={{ position: "relative", borderRadius: 7 }}>
        <div onClick={() => toggleApp(a.key)} className="afn-hover-muted" style={{ position: "relative", display: "flex", alignItems: "center", gap: 8, padding: "5px 8px", borderRadius: 7, cursor: "pointer" }}>
          {a.active && <><span style={{ position: "absolute", inset: 0, borderRadius: 7, background: "color-mix(in oklab, var(--afn-accent) 9%, transparent)", pointerEvents: "none" }} /><span style={{ position: "absolute", left: 0, top: 6, bottom: 6, width: 3, borderRadius: "0 3px 3px 0", background: "var(--afn-accent)", pointerEvents: "none" }} /></>}
          <span style={{ display: "flex", color: a.active ? "var(--afn-fg)" : "var(--afn-muted-fg)" }}><Ico size={15} /></span>
          <span style={{ font: (a.active ? "600" : "500") + " 12px var(--afn-font-sans)", flex: 1, color: a.active ? "var(--afn-fg)" : "var(--afn-muted-fg)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.name}</span>
          {a.badge ? (() => { const Bdg = a.badge!; return <span title="Secured" style={{ display: "flex", color: "var(--afn-muted-fg)" }}><Bdg size={13} /></span> })() : null}
          {pinned.includes(a.key)
            ? <span onClick={(e) => { e.stopPropagation(); togglePin(a.key) }} title="Unpin" style={{ display: "flex", color: "var(--afn-accent)" }}><Star size={13} fill="currentColor" stroke="none" /></span>
            : <span onClick={(e) => { e.stopPropagation(); togglePin(a.key) }} title="Pin" className="afn-pin" style={{ display: "flex", color: "var(--afn-muted-fg)" }}><Star size={13} /></span>}
          <span style={{ display: "flex", color: "var(--afn-muted-fg)" }}>{open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}</span>
        </div>
        {open && (
          <div style={{ padding: "1px 0 5px" }}>
            {a.pages.map((p) => {
              const PIco = p.icon
              return p.active ? (
                <a key={p.path} href={p.url} onClick={() => recordRecent(a.key, p.path, p.title)} style={{ position: "relative", display: "flex", alignItems: "center", gap: 8, padding: "4px 8px 4px 29px", borderRadius: 6, cursor: "pointer", textDecoration: "none", background: "color-mix(in oklab, var(--afn-accent) 6%, transparent)" }}>
                  <span style={{ position: "absolute", left: 17, width: 5, height: 5, borderRadius: "50%", background: "var(--afn-accent)" }} />
                  <span style={{ display: "flex", color: "var(--afn-fg)" }}><PIco size={13} /></span>
                  <span style={{ font: "600 11.5px var(--afn-font-sans)", color: "var(--afn-fg)" }}>{p.title}</span>
                </a>
              ) : (
                <a key={p.path} href={p.url} onClick={() => recordRecent(a.key, p.path, p.title)} className="afn-hover-muted afn-hover-fg" style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 8px 4px 29px", borderRadius: 6, cursor: "pointer", textDecoration: "none", color: "var(--afn-muted-fg)" }}>
                  <span style={{ display: "flex" }}><PIco size={13} /></span>
                  <span style={{ font: "500 11.5px var(--afn-font-sans)" }}>{p.title}</span>
                </a>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  // ── Collapsed rail (desktop only; mobile uses the drawer below) ────────────
  if (rail && !isMobile) {
    return (
      <TooltipProvider delayDuration={200}>
        <style>{AFN_CSS}</style>
        <aside className="afn-shell" data-screen-label="Sidebar rail" style={{ width: 60, flex: "none", display: "flex", flexDirection: "column", alignItems: "center", gap: 7, background: "var(--afn-header-bg)", borderRight: "1px solid var(--afn-border)", padding: "13px 0", height: "100%", boxSizing: "border-box" }}>
          <Tooltip><TooltipTrigger asChild><button onClick={() => { setRail(false); lsSet(RAIL_KEY, false) }} title="askFinz — expand sidebar" style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 34, height: 34, borderRadius: 8, background: "var(--afn-accent)", color: "var(--afn-accent-fg)", border: "1px solid var(--afn-accent)", cursor: "pointer", flex: "none" }}><Building2 size={16} /></button></TooltipTrigger><TooltipContent side="right">Expand sidebar</TooltipContent></Tooltip>
          <Tooltip><TooltipTrigger asChild><button onClick={openSearch} className="afn-hover-muted" title="Search (Ctrl K)" style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 34, height: 34, borderRadius: 8, border: "1px solid var(--afn-border)", background: "var(--afn-card)", color: "var(--afn-fg)", cursor: "pointer", flex: "none" }}><Search size={15} /></button></TooltipTrigger><TooltipContent side="right">Search · Ctrl K</TooltipContent></Tooltip>
          <Tooltip><TooltipTrigger asChild><button onClick={() => setWheelOpen(true)} className="afn-hover-muted" title="Navigation wheel (Ctrl Space)" style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 34, height: 34, borderRadius: 8, border: "1px solid var(--afn-border)", background: "var(--afn-card)", color: "var(--afn-fg)", cursor: "pointer", flex: "none" }}><LocateFixed size={16} /></button></TooltipTrigger><TooltipContent side="right">Navigation wheel · Ctrl Space</TooltipContent></Tooltip>
          <div style={{ height: 1, width: 28, background: "var(--afn-border)", margin: "3px 0", flex: "none" }} />
          <div className="afn-sb-scroll" style={{ flex: 1, width: "100%", minHeight: 0, overflowY: "auto", display: "flex", flexDirection: "column", alignItems: "center", gap: 7 }}>
            {allApps.map((a) => { const Ico = a.icon; const on = a.active; return (
              <Tooltip key={a.key}><TooltipTrigger asChild><button onClick={() => navTo(a.pages[0]?.url ?? "#")} className={on ? "" : "afn-hover-muted"} title={a.name} style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 34, height: 34, borderRadius: 8, border: "1px solid " + (on ? "var(--afn-accent)" : "transparent"), background: on ? "var(--afn-accent)" : "transparent", color: on ? "var(--afn-accent-fg)" : "var(--afn-muted-fg)", cursor: "pointer", flex: "none" }}><Ico size={15} /></button></TooltipTrigger><TooltipContent side="right">{a.name}</TooltipContent></Tooltip>
            ) })}
          </div>
          <Tooltip><TooltipTrigger asChild><button onClick={() => { setRail(false); lsSet(RAIL_KEY, false) }} title={(user?.name || "Account") + " — expand"} style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 32, height: 32, borderRadius: "50%", background: "var(--afn-viz-1)", color: "#fff", font: "600 11px var(--afn-font-sans)", border: "none", cursor: "pointer", flex: "none" }}>{initials}</button></TooltipTrigger><TooltipContent side="right">{user?.name || "Account"}</TooltipContent></Tooltip>
        </aside>
        {wheelOpen && <NavWheel apps={allApps} onGo={navTo} onClose={() => setWheelOpen(false)} />}
        <SearchDialog showTrigger={false} placeholder="Search apps, pages & tickers" items={searchItems} onSearch={financeSearch} />
      </TooltipProvider>
    )
  }

  // ── Expanded sidebar ───────────────────────────────────────────────────────
  // On desktop this is a permanent 268px column in the shell's flex row. On
  // mobile (<768px) it becomes a fixed off-canvas drawer that slides over the
  // content from the left, sitting below the 4rem header, with a tap-to-close
  // backdrop. When closed it is translated off-screen so it reserves no width.
  const asideStyle: CSSProperties = isMobile
    ? {
        width: 268, maxWidth: "85vw", flex: "none", display: "flex", flexDirection: "column",
        background: "var(--afn-header-bg)", borderRight: "1px solid var(--afn-border)", boxSizing: "border-box",
        position: "fixed", top: "4rem", left: 0, bottom: 0, zIndex: 1005,
        transform: mobileOpen ? "translateX(0)" : "translateX(-100%)",
        transition: "transform .28s cubic-bezier(0.4,0,0.2,1)",
        boxShadow: mobileOpen ? "8px 0 40px rgba(0,0,0,.28)" : "none",
        pointerEvents: mobileOpen ? "auto" : "none",
      }
    : {
        width: 268, flex: "none", display: "flex", flexDirection: "column",
        background: "var(--afn-header-bg)", borderRight: "1px solid var(--afn-border)", height: "100%", boxSizing: "border-box",
      }
  return (
    <>
      <style>{AFN_CSS}</style>
      {isMobile && mobileOpen && (
        <div
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
          style={{ position: "fixed", top: "4rem", left: 0, right: 0, bottom: 0, zIndex: 1004, background: "rgba(0,0,0,.45)", backdropFilter: "blur(2px)", WebkitBackdropFilter: "blur(2px)", animation: "afnWheelIn .16s ease-out" }}
        />
      )}
      <aside className="afn-shell" data-screen-label="Sidebar" style={asideStyle}>
        {/* Team switcher */}
        <div style={{ padding: "12px 12px 6px", position: "relative" }}>
          <button onClick={() => setTeamMenuOpen((v) => !v)} className="afn-hover-muted" style={{ width: "100%", boxSizing: "border-box", display: "flex", alignItems: "center", gap: 9, padding: "7px 9px", borderRadius: 8, border: "1px solid var(--afn-border)", background: "var(--afn-card)", cursor: "pointer" }}>
            <span style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 32, height: 32, borderRadius: 8, background: "var(--afn-accent)", color: "var(--afn-accent-fg)", flex: "none" }}><Building2 size={16} /></span>
            <span style={{ display: "flex", flexDirection: "column", lineHeight: 1.18, flex: 1, minWidth: 0, textAlign: "left" }}>
              <span style={{ font: "600 13px var(--afn-font-sans)", color: "var(--afn-fg)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>askFinz</span>
              <span style={{ font: "500 10px var(--afn-font-mono)", letterSpacing: ".02em", color: "var(--afn-muted-fg)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Workspace</span>
            </span>
            <span style={{ display: "flex", color: "var(--afn-muted-fg)", flex: "none" }}><ChevronsUpDown size={15} /></span>
          </button>
          {teamMenuOpen && (
            <>
              <div onClick={() => setTeamMenuOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
              <div style={{ position: "absolute", left: 12, right: 12, top: 48, zIndex: 41, background: "var(--afn-card)", border: "1px solid var(--afn-border)", borderRadius: 11, boxShadow: "0 14px 36px rgba(0,0,0,.24)", padding: 5, animation: "afnPop .14s ease-out" }}>
                <div style={{ padding: "7px 9px 5px", font: "700 9px var(--afn-font-mono)", letterSpacing: ".12em", textTransform: "uppercase", color: "var(--afn-muted-fg)" }}>Teams</div>
                <div className="afn-hover-muted" style={{ display: "flex", alignItems: "center", gap: 9, padding: "7px 8px", borderRadius: 7, cursor: "pointer" }}>
                  <span style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 26, height: 26, borderRadius: 7, border: "1px solid var(--afn-border)", color: "var(--afn-fg)", flex: "none" }}><Building2 size={14} /></span>
                  <span style={{ display: "flex", flexDirection: "column", lineHeight: 1.18, flex: 1, minWidth: 0 }}>
                    <span style={{ font: "600 12.5px var(--afn-font-sans)", color: "var(--afn-fg)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>askFinz</span>
                    <span style={{ font: "500 10px var(--afn-font-mono)", color: "var(--afn-muted-fg)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Workspace</span>
                  </span>
                  <span style={{ display: "flex", color: "var(--afn-accent)", flex: "none" }}><Check size={14} /></span>
                  <span style={{ font: "600 9px var(--afn-font-mono)", color: "var(--afn-muted-fg)", flex: "none" }}>Alt+1</span>
                </div>
                <div style={{ height: 1, background: "var(--afn-border)", margin: "5px 2px" }} />
                <div onClick={() => setTeamMenuOpen(false)} className="afn-hover-muted" style={{ display: "flex", alignItems: "center", gap: 9, padding: "7px 8px", borderRadius: 7, cursor: "pointer" }}>
                  <span style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 26, height: 26, borderRadius: 7, border: "1px dashed var(--afn-border)", color: "var(--afn-muted-fg)", flex: "none" }}><Plus size={15} /></span>
                  <span style={{ font: "600 12.5px var(--afn-font-sans)", color: "var(--afn-muted-fg)" }}>Add team</span>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Search */}
        <div style={{ padding: "2px 12px 8px" }}>
          <button onClick={openSearch} className="afn-hover-border" style={{ width: "100%", boxSizing: "border-box", display: "flex", alignItems: "center", gap: 9, padding: "8px 10px", borderRadius: 8, border: "1px solid var(--afn-border)", background: "var(--afn-card)", color: "var(--afn-muted-fg)", cursor: "pointer", font: "500 13px var(--afn-font-sans)" }}>
            <span style={{ display: "flex" }}><Search size={15} /></span>
            <span style={{ flex: 1, textAlign: "left" }}>Search apps &amp; pages</span>
            <span style={{ font: "600 10px var(--afn-font-mono)", padding: "2px 6px", borderRadius: 5, background: "var(--afn-muted)", color: "var(--afn-muted-fg)" }}>Ctrl K</span>
          </button>
        </div>

        {/* Navigation wheel */}
        <div style={{ padding: "0 12px 10px" }}>
          <button onClick={() => setWheelOpen(true)} className="afn-hover-muted" style={{ width: "100%", display: "flex", alignItems: "center", gap: 9, padding: "9px 11px", borderRadius: 9, border: "1px solid var(--afn-border)", background: "var(--afn-card)", color: "var(--afn-fg)", cursor: "pointer", font: "600 12.5px var(--afn-font-sans)" }}>
            <span style={{ display: "flex", color: "var(--afn-fg)" }}><LocateFixed size={16} /></span>
            <span style={{ flex: 1, textAlign: "left" }}>Navigation wheel</span>
            <span style={{ font: "600 10px var(--afn-font-mono)", padding: "2px 6px", borderRadius: 5, background: "var(--afn-muted)", color: "var(--afn-muted-fg)" }}>Ctrl Space</span>
          </button>
        </div>

        {/* Scrollable nav */}
        <div
          ref={scrollRef}
          onScroll={(e) => { try { sessionStorage.setItem(SCROLL_KEY, String((e.currentTarget as HTMLDivElement).scrollTop)) } catch { /* ignore */ } }}
          className="afn-sb-scroll"
          style={{ flex: 1, overflowY: "auto", padding: "0 8px 14px" }}
        >
          {/* Pinned */}
          {pinnedApps.length > 0 && (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 8px 4px" }}>
                <span style={{ display: "flex", color: "var(--afn-muted-fg)" }}><Pin size={12} /></span>
                <span style={{ font: "700 9px var(--afn-font-mono)", letterSpacing: ".12em", textTransform: "uppercase", color: "var(--afn-muted-fg)" }}>Pinned</span>
              </div>
              {pinnedApps.map((a) => (
                <div key={"pin" + a.key} style={{ position: "relative", borderRadius: 7 }}>
                  {a.active && <><span style={{ position: "absolute", inset: 0, borderRadius: 7, background: "color-mix(in oklab, var(--afn-accent) 9%, transparent)", pointerEvents: "none" }} /><span style={{ position: "absolute", left: 0, top: 6, bottom: 6, width: 3, borderRadius: "0 3px 3px 0", background: "var(--afn-accent)", pointerEvents: "none" }} /></>}
                  <a href={a.pages[0]?.url ?? "#"} onClick={() => recordRecent(a.key, a.pages[0]?.path ?? "/", a.pages[0]?.title ?? a.name)} className="afn-hover-muted" style={{ position: "relative", display: "flex", alignItems: "center", gap: 8, padding: "5px 8px", borderRadius: 7, cursor: "pointer", textDecoration: "none" }}>
                    <span style={{ display: "flex", color: "var(--afn-fg)" }}><a.icon size={15} /></span>
                    <span style={{ font: "500 12px var(--afn-font-sans)", flex: 1, color: "var(--afn-fg)" }}>{a.name}</span>
                    <span onClick={(e) => { e.preventDefault(); e.stopPropagation(); togglePin(a.key) }} title="Unpin" className="afn-unpin" style={{ display: "flex", color: "var(--afn-accent)" }}><Star size={13} fill="currentColor" stroke="none" /></span>
                  </a>
                </div>
              ))}
            </>
          )}

          {/* Recent */}
          {recentApps.length > 0 && (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "12px 8px 4px" }}>
                <span style={{ display: "flex", color: "var(--afn-muted-fg)" }}><History size={12} /></span>
                <span style={{ font: "700 9px var(--afn-font-mono)", letterSpacing: ".12em", textTransform: "uppercase", color: "var(--afn-muted-fg)" }}>Recent</span>
              </div>
              {recentApps.map((a, i) => (
                <a key={"rec" + i} href={a.pages[0]?.url ?? "#"} className="afn-hover-muted" style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 8px", borderRadius: 7, cursor: "pointer", textDecoration: "none" }}>
                  <span style={{ display: "flex", color: "var(--afn-muted-fg)" }}><a.icon size={13} /></span>
                  <span style={{ font: "500 11.5px var(--afn-font-sans)", flex: 1, color: "var(--afn-fg)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.pages[0]?.title}</span>
                  <span style={{ font: "600 9px var(--afn-font-mono)", letterSpacing: ".06em", color: "var(--afn-muted-fg)", whiteSpace: "nowrap" }}>{a.name}</span>
                </a>
              ))}
            </>
          )}

          <div style={{ height: 1, background: "var(--afn-border)", margin: "12px 6px 2px" }} />

          {/* Home (ungrouped) */}
          {model.home && renderAppRow(model.home)}

          {/* Collapsible category groups */}
          {model.groups.map((g) => {
            const open = groups[g.label] !== false
            return (
              <div key={g.label}>
                <div onClick={() => toggleGroup(g.label)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "11px 8px 3px", cursor: "pointer" }}>
                  <span style={{ font: "700 9px var(--afn-font-mono)", letterSpacing: ".12em", textTransform: "uppercase", color: "var(--afn-muted-fg)" }}>{g.label}</span>
                  <span style={{ display: "flex", color: "var(--afn-muted-fg)" }}>{open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}</span>
                  <span style={{ flex: 1 }} />
                  <span style={{ font: "600 9px var(--afn-font-mono)", color: "var(--afn-muted-fg)" }}>{g.apps.length}</span>
                </div>
                {open && g.apps.map((a) => renderAppRow(a))}
              </div>
            )
          })}

          {/* More — Dev Docs, Feedback, askFinz Portal (secured), Account */}
          <div style={{ height: 1, background: "var(--afn-border)", margin: "14px 6px 8px" }} />
          {moreApps.map((a) => renderAppRow(a))}
        </div>

        {/* User footer */}
        <div style={{ flex: "none", borderTop: "1px solid var(--afn-border)", padding: "8px 12px 10px", position: "relative" }}>
          <button onClick={() => setUserMenuOpen((v) => !v)} className="afn-hover-muted" style={{ width: "100%", boxSizing: "border-box", display: "flex", alignItems: "center", gap: 10, padding: "7px 8px", borderRadius: 8, border: "1px solid transparent", background: "transparent", cursor: "pointer" }}>
            <span style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 32, height: 32, borderRadius: "50%", background: "var(--afn-viz-1)", color: "#fff", font: "600 12px var(--afn-font-sans)", flex: "none", overflow: "hidden" }}>
              {user?.avatar ? <img src={user.avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials}
            </span>
            <span style={{ flex: 1, lineHeight: 1.2, minWidth: 0, textAlign: "left" }}>
              <span style={{ display: "block", font: "600 12.5px var(--afn-font-sans)", color: "var(--afn-fg)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{user?.name || "Guest"}</span>
              <span style={{ display: "block", font: "500 10px var(--afn-font-mono)", color: "var(--afn-muted-fg)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{user?.email || "Sign in"}</span>
            </span>
            <span style={{ display: "flex", color: "var(--afn-muted-fg)", flex: "none" }}><ChevronsUpDown size={15} /></span>
          </button>
          {userMenuOpen && (
            <>
              <div onClick={() => setUserMenuOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 46 }} />
              <div style={{ position: "absolute", left: 12, right: 12, bottom: 62, zIndex: 47, background: "var(--afn-card)", border: "1px solid var(--afn-border)", borderRadius: 11, boxShadow: "0 14px 36px rgba(0,0,0,.24)", padding: 5, animation: "afnPop .14s ease-out" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 9px" }}>
                  <span style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 32, height: 32, borderRadius: "50%", background: "var(--afn-viz-1)", color: "#fff", font: "600 12px var(--afn-font-sans)", flex: "none", overflow: "hidden" }}>{user?.avatar ? <img src={user.avatar} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials}</span>
                  <div style={{ flex: 1, lineHeight: 1.2, minWidth: 0 }}>
                    <div style={{ font: "600 12.5px var(--afn-font-sans)", color: "var(--afn-fg)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{user?.name || "Guest"}</div>
                    <div style={{ font: "500 10px var(--afn-font-mono)", color: "var(--afn-muted-fg)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{user?.email || "Sign in"}</div>
                  </div>
                </div>
                <div style={{ height: 1, background: "var(--afn-border)", margin: "4px 2px" }} />
                <div onClick={() => setUserMenuOpen(false)} className="afn-hover-muted" style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 9px", borderRadius: 7, cursor: "pointer" }}>
                  <span style={{ display: "flex", color: "var(--afn-accent)" }}><Sparkles size={15} /></span>
                  <span style={{ font: "600 12.5px var(--afn-font-sans)", color: "var(--afn-fg)" }}>Upgrade to Pro</span>
                </div>
                <div style={{ height: 1, background: "var(--afn-border)", margin: "4px 2px" }} />
                <div onClick={() => navTo(getAppBaseUrl("account.askfinz.ai") || "/")} className="afn-hover-muted" style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 9px", borderRadius: 7, cursor: "pointer" }}>
                  <span style={{ display: "flex", color: "var(--afn-muted-fg)" }}><BadgeCheck size={15} /></span>
                  <span style={{ font: "500 12.5px var(--afn-font-sans)", color: "var(--afn-fg)" }}>Account</span>
                </div>
                <div onClick={() => navTo((getAppBaseUrl("account.askfinz.ai") || "") + "/billing")} className="afn-hover-muted" style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 9px", borderRadius: 7, cursor: "pointer" }}>
                  <span style={{ display: "flex", color: "var(--afn-muted-fg)" }}><CreditCard size={15} /></span>
                  <span style={{ font: "500 12.5px var(--afn-font-sans)", color: "var(--afn-fg)" }}>Billing</span>
                </div>
                <div onClick={() => setUserMenuOpen(false)} className="afn-hover-muted" style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 9px", borderRadius: 7, cursor: "pointer" }}>
                  <span style={{ display: "flex", color: "var(--afn-muted-fg)" }}><Bell size={15} /></span>
                  <span style={{ font: "500 12.5px var(--afn-font-sans)", color: "var(--afn-fg)" }}>Notifications</span>
                </div>
                <div style={{ height: 1, background: "var(--afn-border)", margin: "4px 2px" }} />
                <div onClick={() => { try { clearAuthToken() } catch { /* ignore */ } navTo(getLoginAppUrl() + "/auth/login") }} className="afn-hover-muted" style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 9px", borderRadius: 7, cursor: "pointer" }}>
                  <span style={{ display: "flex", color: "var(--afn-bad)" }}><LogOut size={15} /></span>
                  <span style={{ font: "500 12.5px var(--afn-font-sans)", color: "var(--afn-bad)" }}>Log out</span>
                </div>
              </div>
            </>
          )}
        </div>
      </aside>
      {wheelOpen && <NavWheel apps={allApps} onGo={navTo} onClose={() => setWheelOpen(false)} />}
      <SearchDialog showTrigger={false} placeholder="Search apps, pages & tickers" items={searchItems} onSearch={financeSearch} />
    </>
  )
}
